import { useRef, useState, useEffect } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Presentation, QrCode, DownloadSimple, ChartBar, ChalkboardTeacher } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { usePDFExport } from "@/utils/pdfExport";
import { LEARNING_PATH } from "@/data/learningPath";
import axios from "axios";

const content = {
  en: {
    title: "Show & Tell Script",
    subtitle: "DSLR Advisor — Classroom Presentation (~60 min)",
    admin_only: "Admin Only — Students cannot see this page",
    back: "Back to App",
    download: "Download as PDF",
    setup_title: "Before You Start (Setup)",
    toc_label: "Table of Contents",
    setup: [
      "Open the site on a laptop connected to the TV",
      "Log in with your account beforehand so gear is ready",
      "Have the site on the Dashboard (main page)",
      "Set language to English (or Spanish if your class prefers)",
    ],
    say_label: "Say:",
    do_label: "Do:",
    point_out_label: "Point Out:",
    ref_title: "Quick Reference — Demo Settings",
    ref_cols: ["Scenario", "Lens", "Light", "Type", "Key Setting"],
    total_time: "Total presentation time: ~60 minutes",
    script: [
      {
        part: "CAMERA KNOWLEDGE: ANATOMY, BAYER & HISTORY",
        time: "6 min",
        scenes: [
          {
            title: "Camera Anatomy (DSLR vs Mirrorless)",
            say: "Before settings, let's show WHAT the tool is — so the settings stop feeling abstract.",
            steps: [
              "Learn → Camera Anatomy",
              "Show the SVG diagrams: the DSLR's 45° mirror + pentaprism vs the mirrorless straight-to-sensor path",
              "Walk the 10 components: lens/mount, aperture diaphragm, shutter (mechanical/electronic/global), sensor, processor, IBIS, OVF vs EVF",
              "Finish on the honest DSLR-vs-mirrorless comparison table",
            ],
            pointOut: ["Tie each part to a setting you already teach: the diaphragm = aperture, the curtains = shutter speed."],
          },
          {
            title: "The Bayer Filter — How Cameras See Color",
            say: "Surprising fact: the sensor is colorblind. Watch how it reconstructs color.",
            steps: [
              "Learn → The Bayer Filter",
              "Show the RGGB pattern and explain why there's twice as much green",
              "Explain demosaicing in 4 steps and why RAW is so powerful",
              "Mention X-Trans, Foveon, and monochrome sensors",
            ],
            pointOut: ["Great moment to justify shooting RAW in class."],
          },
          {
            title: "A Brief History of the Camera",
            say: "Let's close the context loop: a 10-part history from 'what is photography?' to AI.",
            steps: [
              "Learn → History of the Camera",
              "Walk the 10-point syllabus on the vertical timeline: (1) what photography is, (2) camera obscura & how it works, (3) Niépce's first permanent photo, (4) Daguerre & the Daguerreotype, (5) Fox Talbot's Calotype vs the Daguerreotype, (6) wet collodion / dry plates / film & rolls, (7) Eastman & Kodak, (8) color: Autochrome & Kodachrome, (9) digital: CCD vs CMOS, (10) today: DSLR, mirrorless, smartphones & AI",
              "Point to #5 — explain WHY Talbot's negative beat Daguerre's one-off (reproducibility = the future)",
              "Download the PDF as a reading handout",
            ],
            pointOut: ["Closing message: gear changes, seeing doesn't."],
          },
          {
            title: "Black & White + Film Photography (Optional)",
            say: "Two craft lessons that deepen how students SEE — perfect for a more advanced class.",
            steps: [
              "Learn → Black & White Photography: why shoot B&W, tonal range + Zone System basics, contrast/shadows/highlights as subject, contrast filters, when color helps vs hurts, and converting vs native B&W",
              "Squint demo: show how squinting collapses a scene into tonal masses — exactly how a B&W image reads",
              "Learn → Film Photography (35mm): the analog revival, film stocks, loading & metering, Sunny 16, developing",
              "Scroll to the new 'Reciprocity Failure & Long Exposures' section — explain why a metered night exposure needs MORE time",
              "Both lessons download as bilingual PDF handouts",
            ],
            pointOut: ["Reinforce: film and B&W obey the same exposure triangle — they just remove the safety net of the screen."],
          },
        ],
      },
      {
        part: "PART 1: THE ADVISOR",
        time: "10 min",
        scenes: [
          {
            title: "Scene 1 — Golden Hour Portrait",
            time: "3 min",
            say: "Imagine you're outside during golden hour and you want to take a portrait of a friend. Let me show you what the app recommends.",
            steps: [
              "In Quick Fill panel, click Canon EOS Rebel T3i (highlights gold)",
              "Click your 50mm f/1.8 lens (auto-fills focal length and aperture)",
              "Lighting Type → Natural",
              "Light Condition → Golden Hour",
              "Time of Day → Sunset",
              "Photo Type → Portrait",
              "Tripod OFF, Movement → Still, DOF → Shallow",
              "Click 'Calculate Settings'",
            ],
            pointOut: [
              "Portraits prioritize aperture first — wider aperture = creamy background blur",
              "ISO Zone badge is green = clean, no noise",
              "Three alternatives: Balanced, Safer, Action — different trade-offs",
              "Scroll to 'From Your Gear' — it picks the best lens from YOUR inventory",
            ],
          },
          {
            title: "Scene 2 — Freeze Sports Action",
            time: "3 min",
            say: "Now you're at a basketball game in bright sunlight. Totally different scenario.",
            steps: [
              "Keep T3i selected",
              "Focal Length → 200, Max Aperture → 5.6 (telephoto lens)",
              "Lens Type → Telephoto",
              "Lighting → Natural, Light Condition → Bright Sun",
              "Time of Day → Noon",
              "Photo Type → Sports",
              "Movement → Fast",
              "Click 'Calculate Settings'",
            ],
            pointOut: [
              "Shutter speed jumped to 1/1000+ — Sports mode freezes motion",
              "ISO may be higher — check the yellow badge (some noise)",
              "Warning: 'consider a faster lens' — f/5.6 lets in less light",
              "Show the Action alternative — even faster shutter for peak moments",
            ],
          },
          {
            title: "Scene 3 — Dark Church Event",
            time: "2 min",
            say: "The tough scenario — inside a dim church for a ceremony. No flash allowed.",
            steps: [
              "Switch back to 50mm f/1.8, Standard lens",
              "Light Condition → Low Light",
              "Time of Day → Night",
              "Photo Type → Event Indoor",
              "Event Scenario → Church",
              "Click 'Calculate Settings'",
            ],
            pointOut: [
              "Aperture wide open (f/1.8), ISO pushed high — red badge = noisy",
              "Camera-specific warning about the T3i's ISO limit",
              "Scroll to Event Guidance: no flash, use available light, tips for church",
            ],
          },
          {
            title: "Scene 4 — Macro with Tripod",
            time: "2 min",
            say: "Let's try extreme close-up of a flower.",
            steps: [
              "Focal Length → 100, Aperture → 2.8",
              "Lens Type → Macro",
              "Light Condition → Cloudy",
              "Photo Type → Macro",
              "Tripod → ON",
              "Movement → Still, DOF → Deep",
              "Click 'Calculate Settings'",
            ],
            pointOut: [
              "Macro DOF is razor thin — app suggests f/8-f/11 for sharpness",
              "Tripod ON = slower shutter, lower ISO = cleaner image",
              "Macro warning about minimum focus distance — real-world advice",
            ],
          },
        ],
      },
      {
        part: "AI: DESCRIBE YOUR SHOT",
        time: "3 min",
        scenes: [
          {
            title: "AI Mode — Describe Your Shot",
            say: "For beginners who don't know the jargon yet, the AI translates a normal sentence into manual settings.",
            steps: [
              "At the top of the calculator, flip the toggle from 'Manual' to 'Describe Your Shot'",
              "Type a shot in plain language (e.g. 'fast-moving soccer game on a bright sunny afternoon') or tap an example",
              "Click 'Get AI Settings' — show Aperture, Shutter, ISO, the scenario, the recommended lens, and pro tips",
              "Point out the 'From your gear' badge: the AI picked the best lens the student owns in My Gear",
              "Click 'Apply to Manual Form' — the AI fills the Manual calculator; now fine-tune, Calculate, and Save to History",
            ],
            pointOut: [
              "Key message: the AI is the bridge from everyday language to manual control — not a replacement for learning.",
            ],
          },
        ],
      },
      {
        part: "SAVE & DOWNLOAD",
        time: "1 min",
        scenes: [
          {
            title: "Save Results & Download PDF",
            say: "Every calculation can be saved and downloaded as a worksheet.",
            steps: [
              "Click 'Save to History' (bookmark icon on results)",
              "Navigate to History tab",
              "Click expand arrow on the saved entry",
              "Click the PDF download button",
              "Show the PDF — clean white page, all settings, ready to print",
            ],
            pointOut: [],
          },
        ],
      },
      {
        part: "PART 2: MY GEAR INVENTORY",
        time: "3 min",
        scenes: [
          {
            title: "Show Gear Inventory",
            say: "The app lets you save all your equipment for personalized recommendations.",
            steps: [
              "Click 'My Gear' in navbar",
              "Show saved camera (Canon T3i) and lens (50mm f/1.8)",
              "Point out detail fields: model, serial, condition, price",
              "Show Lighting (speedlight) and Support (tripod) sections",
              "Open the Lighting category — point out the dedicated rows for Light Meters (Sekonic L-308X) and Color Checkers (Datacolor SpyderCHECKR, X-Rite ColorChecker). 'Everything that touches color and exposure lives in one tab.'",
              "Scroll to the new 'Film Cameras' section — add an analog body like the Canon AE-1 Program. 'Film bodies have no digital EXIF, but you can still inventory them here alongside your digital gear.'",
              "Click 'Download Excel' — exports inventory as spreadsheet",
            ],
            pointOut: [],
          },
        ],
      },
      {
        part: "PART 3: PHOTOGRAPHY TOOLS",
        time: "5 min",
        scenes: [
          {
            title: "Tools & Fundamentals Tour",
            say: "The Tools tab is your photography classroom in a box.",
            steps: [
              "Click 'Tools' in navbar",
              "Exposure Triangle — show the 3 pillars",
              "DOF Calculator — change aperture from 2.8 to 11, show DOF change",
              "Handheld Speed Calculator — enter focal length for safe speed",
              "Sunny 16 Reference — classic exposure rules",
              "Sunny-16 / Hand-Meter Helper — pick a scene + ISO to get aperture & shutter (great for film or a dead meter)",
              "Reciprocity Failure Calculator — enter a metered time (e.g. 8s) + film stock to get the corrected long exposure (~15s on Ilford)",
              "EXIF Reader — pick a student's photo and reveal the settings it was shot with (read in the browser only, never uploaded); then tap 'Use in Settings Calculator' to compare with the app's recommendation",
              "Scroll to Fundamentals:",
              "  • Rule of Thirds — click grid intersections for tips",
              "  • Color Temperature — show Kelvin scale warm to cool",
              "  • Composition — 6 technique cards",
              "  • Technical Mastery — focus modes, metering, white balance",
              "  • Post-Processing — 6 steps from crop to export",
            ],
            pointOut: [],
          },
          {
            title: "Photography Quiz",
            say: "Who wants to test their knowledge?",
            steps: [
              "Click 'Start Quiz'",
              "Answer 2-3 questions live with the class",
              "Show explanation after each answer",
              "12 questions total — students try on their own",
            ],
            pointOut: [],
          },
          {
            title: "Lesson Quizzes (Learn → Quizzes)",
            say: "Every lesson now ends with a short quiz — let's check what we just learned.",
            steps: [
              "Scroll to the bottom of the lesson you just taught — 'Check what you learned' (5 questions)",
              "Answer one question together; show the green/red feedback and the explanation",
              "Open Learn → Quizzes to show all 27 lesson quizzes in one place",
              "Students finish the quiz on their own and compare scores (only perfect 5/5 results are saved, as badges)",
              "A perfect 5/5 earns a badge — a green medal appears next to that lesson in the Learn menu",
              "Tap 'Print (PDF)' to download a class handout with a separate Answer Key page",
              "Open Learn → Final Exam: 30 random questions, pass at 80% for a downloadable, shareable certificate",
              "After the exam, show 'Your weak spots': the lessons behind each missed question, with the right answers and a 'Retake its quiz' button",
              "Tap 'Practice only my missed questions' for a quick round — wrong answers repeat until all are right, then retake the full exam",
              "Point out the blue 'Practice Streak ×N' badge and the Monthly Class Champion card (gold trophy + ready-to-post image)",
              "Teacher: in 'Teacher Progress View' review 'Class weak spots' to decide what to reteach, and 'Download CSV' for grades",
              "Teacher: create a class in 'Teacher Progress View' → Classes, share its code; filter progress, weak spots and CSV by class, and 'Print review quiz' with the 10 most-missed questions",
              "Show the Hall of Fame from the Monthly Champion card",
              "Teacher: Presenter → 'Teacher Progress View' shows every student's project checklist progress",
              "Show the Class Leaderboard on Learn → Quizzes — students opt in with 'Show me as …' (first name + last initial only)",
              "After passing the exam, 'Ready-to-post image (1080×1080)' gives a square certificate for Galería Social / Instagram",
            ],
            pointOut: ["Answer options are shuffled on every attempt — 'Try again' gives a fresh order"],
          },
        ],
      },
      {
        part: "PART 4: PROJECTS",
        time: "3 min",
        scenes: [
          {
            title: "Photography Projects",
            say: "Ready to practice? These are your hands-on assignments.",
            steps: [
              "Click 'Projects' in navbar",
              "Expand Food Photography — Beginner. Show objective, settings, tips",
              "Expand Sports & Action — Advanced. Fast shutter, burst mode",
              "Expand Night & Long Exposure — tripod, low ISO, 30-second exposures",
              "Point out difficulty badges: Green=Beginner, Gold=Intermediate, Red=Advanced",
              "Open a project and show the new Challenge, then tick a box in the Deliverables Checklist — progress saves to the student's account (see the counter and the 'Your progress' summary at the top)",
              "Scroll to 'Deliver your work to Galería Social' — students post their finished photos on galeriasocial.com themselves for feedback and inspiration",
              "Point out the 'Share to Galería Social' button next to Suggested Settings (it also appears on Calculator results)",
            ],
            pointOut: [],
          },
        ],
      },
      {
        part: "PART 5: PHOTO CAREERS",
        time: "2 min",
        scenes: [
          {
            title: "Career Paths",
            say: "Photography isn't just a hobby — it's a career.",
            steps: [
              "Click 'Careers' in navbar",
              "Expand Wedding Photographer — show work, skills, salary",
              "Expand Sports Photographer — 'If you love action, this is your path'",
              "Expand Forensic Photographer — 'Bet you didn't know this existed'",
              "20 career paths total — browse on your own time",
            ],
            pointOut: [],
          },
        ],
      },
      {
        part: "PART 6: LIGHTING TECHNIQUES",
        time: "3 min",
        scenes: [
          {
            title: "Portrait Lighting Patterns",
            say: "Great photographers don't just press the button — they shape light. Let's look at 10 classic portrait lighting patterns.",
            steps: [
              "Click 'Lighting' in the navbar",
              "Expand 'Rembrandt' — point out the triangle of light on the cheek",
              "Expand 'Butterfly / Paramount' — classic Hollywood glamour lighting",
              "Expand 'Split' — half shadow, half light, dramatic mood",
              "Expand 'Rim / Backlight' — separates subject from background",
              "Scroll to 'Light Kits' — 8 gear types explained (speedlights to natural window light)",
              "Click 'Download as PDF' — classroom-ready handout",
            ],
            pointOut: [
              "Every pattern shows: description, subject angle, key light position, and quick-setup tips",
              "Perfect reference for practice at home — start with one pattern, master it, move to the next",
              "Full EN/ES — useful for bilingual classmates",
            ],
          },
        ],
      },
      {
        part: "PART 7: GOLDEN HOUR & SUN TIMES",
        time: "3 min",
        scenes: [
          {
            title: "Plan Your Shoot Around the Magic Light",
            say: "Professional photographers don't shoot whenever — they shoot when the light is best. This page does all the math for you.",
            steps: [
              "Click 'Golden Hour' in the navbar",
              "First time: tap 'Use my location' — the page detects your city automatically",
              "Or use the 'Manual' tab and search 'Madrid' or any other city",
              "Point to the four 'Best Shooting Windows' cards — Morning Blue Hour, Morning Golden Hour, Evening Golden Hour, Evening Blue Hour",
              "Each card shows the time range, duration, and a one-line tip — that's your shooting checklist for the day",
              "Scroll down to the full Daily Sun Timeline — 13 phases with photographer's tips for each",
              "Show the Quick Stats — Sunrise + Sunset times with compass bearings (E/W and degrees)",
              "Tap the prev/next arrows or the date picker to plan a shoot for tomorrow or next weekend",
              "Scroll to the Moon section — moonrise, moonset, current phase, illumination percent",
            ],
            pointOut: [
              "All times calculated locally on your phone — no internet needed once the page is loaded",
              "Compass bearings tell you exactly where the sun will rise / set — perfect for scouting",
              "Use it the night before a shoot to set your alarm for golden hour",
              "Moon section is a bonus for night photography and astrophotography planning",
            ],
          },
        ],
      },
      {
        part: "PART 8: PHOTOGRAPHY GLOSSARY",
        time: "2 min",
        scenes: [
          {
            title: "Bilingual Photography Glossary",
            say: "If you forget a term, you shouldn't have to open a textbook — you just type.",
            steps: [
              "Click 'Glossary' in the navbar",
              "Type 'aperture' in the search — live filter",
              "Click through a few entries to show English + Spanish definitions side-by-side",
              "46+ terms covering exposure, lenses, lighting, composition, post-production",
              "Click 'Download as PDF' — full printed glossary for offline study",
            ],
            pointOut: [
              "Great for students whose first language isn't English",
              "Works during a shoot — search on your phone between shots",
            ],
          },
        ],
      },
      {
        part: "PART 9: STUDIO SETUP & BUDGET CALCULATOR",
        time: "5 min",
        scenes: [
          {
            title: "Studio Setup Guide",
            say: "Maybe the most valuable page if you're dreaming of your own studio. Everything you need to know to build one — small or mid-size.",
            steps: [
              "Click 'Studio' in the navbar",
              "Scroll past the Budget Calculator (we'll come back) to Space Planning",
              "Scroll down through the 9 sections: Space, Lighting Kit (with photos), Triggers, Light Meters, Modifiers, Backgrounds & Green Screen, Support Gear",
              "Point out the 4 Green Screen Tips block — subject distance, flat lighting, clothing warnings",
              "Scroll to the 2 SVG layout diagrams — Small (3-light) and Mid-Size (5-light)",
              "Show the Starter Checklists — Minimum Viable (~$1,200) and Mid-Size (~$4,000+)",
            ],
            pointOut: [
              "All equipment cards have real reference photos",
              "The layout diagrams label Key / Fill / Rim / Background lights plus Subject and Camera positions",
              "Download as PDF gives a complete studio planning document",
            ],
          },
          {
            title: "Budget Tier Calculator — Live Demo",
            say: "Now the killer feature — we tell the app what you can afford, it tells you exactly what to buy first.",
            steps: [
              "Scroll to the top of Studio, back to the Budget Calculator",
              "Drag the slider to $500 → 'Starter Studio' tier, 8 items appear in green",
              "Drag to $1,500 → 'Small Studio', 17 items fit, $1,485 used",
              "Drag to $4,000 → 'Mid-Size Studio', 31 items fit",
              "Drag to $8,000 → 'Advanced Studio', 40 items fit",
              "Drag to $12,000 → 'Full Pro Kit', all 43 items fit, green 'complete kit' banner appears",
              "The tip under the slider changes to match each tier — personalized advice",
            ],
            pointOut: [
              "43 gear items ordered by priority — every dollar goes to the most impactful next piece",
              "The 'Save Up For Next' list shows the stretch items, so you always know what's coming",
              "The PDF snapshot captures your current budget + tier + lists — share it with family when you ask for gear!",
            ],
          },
          {
            title: "Shopping List (Signed-In Users)",
            say: "One more trick — if you already own some of this gear, the calculator adapts.",
            steps: [
              "Tick the checkbox next to an item you 'already own' (e.g. Speedlight #1)",
              "Watch the item move to the 'Already Owned' section with a strikethrough",
              "Notice the next item in the priority list moves up into 'Within Your Budget'",
              "The header shows 'Owned: 3 / 43 ($425 saved)'",
              "Log out and log back in — the list persists across devices",
            ],
            pointOut: [
              "The calculator excludes owned items from the math, so your budget goes further",
              "Saved to the cloud per user — works on phone, tablet, desktop",
              "Great for ongoing planning — you come back in 3 months after buying gear, the plan updates automatically",
            ],
          },
        ],
      },
      {
        part: "PART 10: PRINTING & OUTPUT",
        time: "3 min",
        scenes: [
          {
            title: "From Digital File to Print",
            say: "Great photos deserve to leave the screen. This page is the bridge between a RAW file and a framed print on a wall.",
            steps: [
              "Click 'Printing' in the navbar",
              "Scroll through the reference table — 4x6 through 24x36 poster, with pixel requirements at 300 / 240 / 180 DPI",
              "Open the Print Calculator — enter image dimensions (show 6000 x 4000 as example)",
              "Point out the three results: 300 DPI, 240 DPI, 180 DPI max print sizes",
              "Scroll to the aspect ratio diagrams — show how 8x10 loses ~17% of a 3:2 DSLR frame",
              "Show the 8-step Digital Processing Workflow cards",
              "Scroll to Framing & Matting + Color Management + Print Lab Checklist",
              "Click 'Download as PDF' — complete printing reference for the lab counter",
            ],
            pointOut: [
              "Students with lower-MP cameras can quickly see their print-size ceiling",
              "Classmates can plan composition with crop in mind — no more heads cut off at 8x10",
              "The Print Lab Checklist is a one-page protection against re-printing orders (saves $$$)",
              "Full EN/ES — useful for working with Spanish-language labs",
            ],
          },
        ],
      },
      {
        part: "PART 11: ADVANCED TECHNIQUES (Histogram, Metering, Exposure Comp, Crop Factor, Shots, Calibration, Tether, Filters, Compression, Focus, WB/DR, Gray Card, Smartphone, Posing, Maintenance, Contracts)",
        time: "41 min",
        scenes: [
          {
            title: "The Histogram — fundamental exposure tool",
            time: "3 min",
            say: "Before we cover anything else technical, every student needs to befriend the histogram. The LCD lies — the histogram doesn't. It works the same on Canon, Nikon, Sony, and Fuji because it reads the sensor, not the brand.",
            steps: [
              "Open Learn → The Histogram from the navbar dropdown",
              "Show the 5-zone Tonal Map gradient at the top — Black Point, Shadows, Midtones, Highlights, White Point",
              "Scroll to the red 'The Wall' clipping alert and read the 'DATA TOUCHING THE EDGE IS GONE FOREVER' line out loud",
              "Show the 'no perfect bell-curve' note",
              "Scroll through the 3 scene examples (Low Key / High Key / High Contrast) — for each, point at the photo, then point at the histogram, then read the tip",
              "Show the Blinkies section and the Universal Hardware Guide (DISP/INFO button works on every brand)",
              "Click 'Download as PDF' for the classroom handout",
            ],
            pointOut: [
              "Most important takeaway: there is NO 'good' or 'bad' histogram shape — only one that matches the mood of the scene",
              "If you must clip something, clip the shadows. Lost shadow detail looks like 'mood'; blown highlights look like a mistake",
              "Tell students to spend 5 minutes ONCE in their camera menu enabling histogram on Live View AND Playback — they will use it on every shot from then on",
              "The Blinkies (Highlight Alert) tells you WHERE in the image the histogram says clipping is happening — use both together",
            ],
          },
          {
            title: "Metering Modes — how the camera measures light",
            time: "3 min",
            say: "The histogram shows you the result. Metering Modes decide how the camera produces that result. Same lesson for Canon, Nikon, Sony, and Fuji users.",
            steps: [
              "Open Learn → Metering Modes from the navbar",
              "Show the 4 mode diagrams — Matrix (whole frame lit up), Center-Weighted (glowing ellipse), Partial (medium dot), Spot (tiny dot)",
              "Point out the brand-name mapping: Matrix = Nikon, Evaluative = Canon, Multi-segment = Sony/Fuji",
              "Read out the Pro Workflow box: pick mode → half-press → check histogram → apply EV → shoot",
              "Show the brand button paths card for Canon/Nikon/Sony/Fuji",
              "Click 'Download as PDF'",
            ],
            pointOut: [
              "80% of working pros use Matrix/Evaluative + Exposure Compensation — they almost never switch modes",
              "Spot is the answer when Matrix fails — concerts, backlit faces, the moon",
              "On most bodies, Spot follows the active AF point. If the AF point is on the sky, you are spot-metering the sky, not your subject",
              "Reset to Matrix between sessions — leaving Spot enabled is the #1 source of mystery exposure errors the next morning",
            ],
          },
          {
            title: "Exposure Compensation — the +/- override",
            time: "4 min",
            say: "Best topic in the entire app for fixing exposure on the fly. The camera's meter thinks every photo should average to 18% gray — which ruins snow, ruins night, ruins backlit portraits. Exposure Compensation is the override.",
            steps: [
              "Open Learn → Exposure Compensation",
              "Read 'The Why' — the 18% gray problem with 4 example bullets (snow goes gray, black tuxedo goes gray, backlit face goes silhouette, sunset foreground goes dark)",
              "Scroll to the INTERACTIVE SLIDER section — this is the highlight",
              "Drag the slider from 0 → +2 EV. The right preview brightens VISIBLY while the left stays the same. Read the EV label live.",
              "Drag the slider from +2 → −2 EV. The right preview darkens. Pause and let students compare.",
              "Show the mini histogram below the slider — it shifts left/right as you drag. This IS what happens inside the camera.",
              "Scroll to the 8 real-world scenarios grid — read out the snow row (+1 to +1.7 EV) and the concert row (−1 to −2 EV)",
              "Show the brand button paths (Canon, Nikon, Sony, Fuji) and the Auto-Mode compatibility table",
              "Click 'Download as PDF' for the classroom handout",
            ],
            pointOut: [
              "If the histogram piles against the LEFT wall → add +EV. If it piles against the RIGHT wall → subtract −EV. This is the same lesson you just taught in the Histogram section.",
              "Most beginners under-compensate. Tell them: +2 EV on snow is normal, not extreme.",
              "Sony / Fuji mirrorless show a LIVE preview that updates as you turn the dial — they see what the photo will look like before pressing the shutter",
              "Reset EV to 0 when the lighting changes or you move to a new scene — otherwise you will accidentally underexpose your next family photo",
            ],
          },
          {
            title: "Focal Length & Crop Factor",
            time: "2.5 min",
            say: "A 50mm lens is not a 50mm lens on every camera. Sensor size changes everything — angle of view, depth of field, what gear you should buy.",
            steps: [
              "Open Learn → Crop Factor",
              "Show the SVG sensor comparison diagram — 6 concentric rectangles, all centered, all drawn to scale",
              "Point out: Full Frame = baseline (1.0x), APS-C Canon (1.6x) is slightly smaller than APS-C Nikon/Sony/Fuji (1.5x), Micro 4/3 is 2.0x",
              "Show the crop-factor table — a 50mm lens 'becomes' 80mm on Canon APS-C, 75mm on Nikon APS-C, 100mm on Micro 4/3",
              "Scroll to the Pros/Cons cards — smaller sensors = cheaper + free 'reach' for wildlife; bigger sensors = wider wide-angle + better low light + shallow DOF",
              "Show the lens designation map: RF/RF-S (Canon), FX/DX (Nikon), FE/E (Sony), XF/GF (Fuji)",
              "Click 'Download as PDF' for the buyer's reference",
            ],
            pointOut: [
              "An 85mm f/1.8 on Full Frame is the classic 'portrait look'. To match that on APS-C you would need a 56mm f/1.2 — which exists, but costs more",
              "Sports/wildlife shooters often PREFER APS-C because the crop factor gives them 'free reach' (a 300mm lens behaves like 450mm)",
              "DO NOT chase full-frame because magazines say so. Modern APS-C bodies produce gorgeous professional images and save thousands on lenses",
              "If you put a 'full-frame' lens on an APS-C body, it works fine (just crops). If you put an 'APS-C only' lens on a full-frame body, you get heavy black corners (vignetting).",
            ],
          },
          {
            title: "Types of Shots — the visual language",
            time: "1.5 min",
            say: "Now: every photographer needs to speak the language of framing — the same person reads completely differently in a close-up versus a wide shot.",
            steps: [
              "Open Learn → Types of Shots from the navbar dropdown",
              "Scroll through all 8 cards, top to bottom",
              "Stop at Medium Shot — show the diagram crop overlay vs. the AI photo example",
              "Show the 3 pro tips on a couple of cards (lens choice, common cropping mistakes)",
              "Click 'Download as PDF'",
            ],
            pointOut: [
              "8 standard shots in cinematic order — extreme close-up to extreme wide",
              "Yellow dashed crop window on each diagram = exactly what the camera captures",
              "Photo + diagram side-by-side teaches both the abstract framing AND the real-world look",
              "PDF handout works great as an assignment: shoot one example of each shot type in a single session",
            ],
          },
          {
            title: "Monitor Calibration — Live Tests",
            time: "2 min",
            say: "If your screen lies, your prints will lie. This page teaches calibration AND lets you test your own monitor right now.",
            steps: [
              "Open Learn → Monitor Calibration",
              "Skim the Why / Color Spaces / Targets sections quickly",
              "Scroll to 'Live Monitor Tests'",
              "Run all 6 tests on the classroom screen: black level, white level, grayscale ramp, gamma, color uniformity, sharpness chart",
              "Read the instruction below each test out loud — students self-diagnose",
              "Have a few students open the page on their phones and run the same tests",
            ],
            pointOut: [
              "These tests use only standard CSS — no plugins, no downloads, works on any screen",
              "Banding in the grayscale ramp = 6-bit panel limitations on cheap monitors",
              "The classroom TV usually fails the 'gamma 2.2 row blends best' test — great real-world demo",
              "Students with their own laptops can recalibrate at home using the built-in OS tools listed",
            ],
          },
          {
            title: "Tether Photography",
            time: "1.5 min",
            say: "If you ever shoot for a client, learn to tether. Studio, product, fashion, classroom demos — a cable to the laptop changes everything.",
            steps: [
              "Open Learn → Tether Photography",
              "Show the green Pros / red Cons cards",
              "Scroll to the Software comparison — point out Capture One (the studio standard) and the free first-party tools (Canon EOS Utility, Nikon NX Tether)",
              "Scroll to the Cable section — emphasize the JerkStopper anchor",
              "Show the 8-step setup checklist",
              "Click 'Download as PDF' for the studio reference",
            ],
            pointOut: [
              "Buy ONE good Tether Tools cable ($25-70) — it pays for itself the first time it doesn't fail mid-shoot",
              "If you ever drop a $1500 camera by tripping on a cable, the JerkStopper is what would have saved you",
              "Capture One Express is FREE for Fuji users — point that out",
              "For classrooms specifically: tether the instructor's camera so every student sees what the lens sees in real time",
            ],
          },
          {
            title: "Hardware Filters: UV, CPL, ND",
            time: "2.5 min",
            say: "Modern editing can fix exposure, color, and noise — but it CANNOT remove a reflection, deepen a washed-out sky, or freeze water motion. Three filters cover almost every situation.",
            steps: [
              "Open Learn → Hardware Filters",
              "Show the UV section — emphasize that for digital sensors this is purely PROTECTION ($30-50 sacrificial glass)",
              "Scroll to the CPL Before/After slider. DRAG IT slowly — washed-out sky on the left becomes deep blue on the right, lake reflections vanish revealing rocks underneath",
              "Scroll to the ND Before/After slider. DRAG IT — frozen waterfall on the left becomes silky-smooth on the right",
              "Show the 5 ND strengths card (ND2 / ND8 / ND64 / ND1000 / Variable)",
              "Click 'Download as PDF'",
            ],
            pointOut: [
              "Cheap UV filters DEGRADE image quality (flare/ghosting) — buy B+W or Hoya HD multi-coated, or skip UV entirely",
              "Circular Polarizer is the ONE filter every photographer should own. Cannot be replicated in software.",
              "ND64 (6 stops) is the most-used neutral density — buy this BEFORE the 10-stop",
              "Variable NDs are convenient but cheap ones produce a dark X-pattern at the extremes — avoid the $20 ones",
              "Buy ONE filter sized for your LARGEST lens, then use step-up rings — saves money long-term",
            ],
          },
          {
            title: "Lens Compression — Distance, NOT Focal Length",
            time: "2 min",
            say: "This is the single most-repeated lie in photography tutorials: '200mm compresses the background.' Wrong. The LENS magnifies. The CAMERA-TO-SUBJECT DISTANCE creates the perspective. Today we end this myth forever.",
            steps: [
              "Open Learn → Lens Compression",
              "Read the rose-colored 'Myth' card out loud, then the green 'Truth' card",
              "Scroll to the Before/After slider — DRAG IT. Left: 24mm shot from 1m away, mountains tiny. Right: 200mm shot from 30m back, SAME person, mountains HUGE behind",
              "Pause. Let the class internalize: 'the lens did nothing — the photographer's FEET did this'",
              "Show the 'How pros use this' bullet list — emphasize the portrait + group photo rules",
              "Click 'Download as PDF'",
            ],
            pointOut: [
              "Wide angles distort faces at close distance — this is GEOMETRY, not a lens flaw. Step back, switch to 85mm, problem solved.",
              "Group photo rule: NEVER shoot a group of people with a 24mm lens up close. Edge people look fat. Back up, use 50-85mm.",
              "If you want mountains to look HUGE behind your subject: back up FAR and use a telephoto. Distance is the magic, not the glass.",
              "Lightroom can fix lens distortion (barrel/pincushion) but NOT perspective distortion. The only fix is moving your feet next time.",
            ],
          },
          {
            title: "Advanced Focus: Back-Button Focus & AI Eye-AF",
            time: "3 min",
            say: "Two techniques separate amateurs from professionals: decoupling focus from the shutter, and leveraging AI eye-tracking. Configure once, use forever.",
            steps: [
              "Open Learn → Advanced Focus",
              "Explain BBF in plain words: 'Right now, half-press shutter = focus. We're going to move that to the AF-ON button on the back, so the shutter ONLY shoots.'",
              "Read the 'Why pros do this' bullets — focus and recompose, track bursts without re-focusing, eliminate shutter lag",
              "Show the brand-specific setup cards (Canon / Nikon Z / Sony / Fujifilm) — pick the student's brand and walk through it live",
              "Scroll to AI Eye-AF — explain it as 'the camera finds the eye automatically and tracks it, even at f/1.4 with razor-thin DOF'",
              "Show the brand-specific Eye-AF setup cards",
              "End on the green Combo card — 'BBF + Eye-AF together is what every modern wedding/portrait pro uses'",
              "Click 'Download as PDF'",
            ],
            pointOut: [
              "The first week of BBF feels weird. After 1 week, students never go back. Muscle memory is permanent.",
              "Eye-AF works through glasses, partial face cover, and masks — but NOT closed eyes. For sleeping newborns, use single-point AF on the eyebrow",
              "Sports/wildlife shooters: enable Animal Eye AF or Bird Eye AF — they're separate modes on modern bodies",
              "For Sony A7 IV+, the 'Subject Recognition Frame' overlay shows you LIVE which eye the camera is tracking — point this out as a demo",
            ],
          },
          {
            title: "White Balance & Dynamic Range",
            time: "2 min",
            say: "Two technical foundations that decide whether your photos have natural skin tones, blown skies, or muddy shadows. The Kelvin scale and the DR ceiling of your sensor.",
            steps: [
              "Open Learn → White Balance & DR",
              "Show the Kelvin gradient strip — warm orange (1700K candle) on the left to cool blue (10,000K sky) on the right",
              "Read the GOLDEN RULE box: 'LOW K to fight WARM light, HIGH K to fight COOL light' — this is counterintuitive and trips up most students",
              "Show the 5 WB strategies card (Auto / Presets / Custom / Manual K / RAW)",
              "Scroll to Dynamic Range — read the 'full-frame = 14-15 stops, eyes = 20+' stat",
              "Show the 5-step DR home-test instructions — students can run this with their own camera tonight",
              "Show the Bracketing & HDR section — demonstrate the workflow",
              "End on the orange Histogram Connection card — link directly back to the histogram lesson",
              "Click 'Download as PDF'",
            ],
            pointOut: [
              "Rule for life: shoot RAW + Auto WB for 95% of situations. For the other 5%, invest in an X-Rite ColorChecker ($100)",
              "For VIDEO: lock Manual Kelvin once at the start of a take, never change it during the take — clip-to-clip color shifts ruin the edit",
              "When a scene exceeds your sensor's DR (sunset behind mountains), bracket-and-merge — don't fight physics, embrace HDR",
              "Photonstophotos.net has a FREE database of every modern camera's DR — students should look up their own body",
            ],
          },
          {
            title: "The Gray Card: Color Accuracy & Exposure Precision",
            time: "3 min",
            say: "Today we cover the cheapest, most under-used tool in professional photography — an $18 piece of cardboard that solves four problems no software can fix: meter accuracy in high-contrast scenes, white balance under ANY light, color sync between multiple cameras, and focus calibration verification.",
            steps: [
              "Open Learn → The Gray Card",
              "Show the 3-tile black / 18% gray / white visual — explain that every meter on Earth is calibrated to that exact middle tone",
              "Scroll to 'Overcoming the Internal Meter Trap' — go through the 4 worked examples: bride in white, black cat, snowy landscape, backlit silhouette. Emphasize each TRAP (rose) and FIX (emerald).",
              "Read the 5-step Spot Metering + AE-Lock workflow",
              "Scroll to 'Custom White Balance Mastery' — walk through the 6-step in-camera Custom WB procedure, then point at the 4 genre callouts (product / portrait / lifestyle / real estate)",
              "Scroll to 'Multi-Camera Color Synchronization' — explain the modern scenario (DSLR + iPhone + drone) and walk through the 6-step Lightroom workflow",
              "Scroll to 'Managing High-Gloss and Reflective Surfaces' — emphasize color spill detection (red couch into white perfume bottle = pink contamination)",
              "Scroll to 'Focus & Sharpness Verification' — point at the AFMA/AF Fine Tune/AF Micro Adj brand callouts",
              "Scroll to 'Leveling Up: The 24-Patch Color Checker (Datacolor SpyderCHECKR 24)' — point at the visual 24-patch layout. Explain it's the gray card upgraded with 18 calibrated color patches built in.",
              "Read the WHEN-to-use list — emphasize this is NOT every shoot. Only product, studio portrait, fine art reproduction, video matching, and exact-color client jobs.",
              "Walk the 5-step FIELD workflow: reference frame at start of each lighting setup, file-name the reference, re-shoot when lighting changes.",
              "Walk the 7-step LIGHTROOM workflow: gray-patch white balance, export DNG, drag into SpyderCHECKR software, generate custom DCP profile, apply profile to reference, sync to all shots.",
              "End on the orange Pro Tip: '95% of soft photos at f/11 are a focus problem, NOT diffraction'",
              "Click 'Download as PDF'",
            ],
            pointOut: [
              "The gray card is the difference between an amateur and a pro. Pros pull it out in the first 30 seconds of a shoot. Amateurs never own one.",
              "For product/commercial e-commerce work, custom WB from a card is NON-NEGOTIABLE. Auto WB ruins color accuracy on white t-shirts, food, and any branded packaging.",
              "Multi-camera sync is the 2026 superpower: phones now shoot competent stills, drones provide aerials, and clients expect everything color-matched. One card frame per camera = a unified edit.",
              "For brides and snow: meter on a card in the SAME light, lock the exposure with AE-Lock, then shoot. This single technique alone saves more shots than every Lightroom slider combined.",
              "The SpyderCHECKR 24 is the natural upgrade path from a basic gray card. Tell students: gray card lives in the camera bag, SpyderCHECKR lives in the studio kit. Carry both — they solve different problems.",
              "Free SpyderCHECKR software (included with the card) creates custom DCP profiles for Lightroom. Students who shoot products or fine art reproduction should buy a SpyderCHECKR (~$45-50) before any new lens.",
            ],
          },
          {
            title: "Smartphone Photography",
            time: "4 min",
            say: "Today's students reach for their phone before they reach for a DSLR. Modern flagship phones shoot 12-50 MP with computational HDR that rivals dedicated cameras for daily work. This lesson teaches them to STOP being amateurs with their phone.",
            steps: [
              "Open Learn → Smartphone Photography",
              "Point at the quick-jump sub-nav at the top — explain it lets students jump directly to any of 6 lessons",
              "Open lesson 1: Mastering Mobile Exposure & Focus. Walk the iOS column (yellow square + sun-slider + AE/AF Lock) and the Android column (focus + brightness slider + tap-and-hold lock) side by side.",
              "Open lesson 2: Pro Mode. Explain that iOS needs a third-party app (Halide, Lightroom Mobile) while Samsung/OnePlus have built-in Pro mode and Pixel needs Open Camera.",
              "Open lesson 3: Composition Rules. Demonstrate enabling 3x3 grid lines on iOS (Settings → Camera → Grid) and Android stock camera.",
              "Open lesson 4: Lens Selection. Pull out a phone live — show .5x, 1x, 2x/3x physical lens buttons. Hammer the rule: 'never pinch-zoom beyond the longest physical lens — that's software garbage'.",
              "Open lesson 5: Portrait Mode. Demonstrate the 2-8 ft distance sweet spot, iOS lighting styles, and Pixel's retroactive bokeh adjustment in Edit → Tools → Blur.",
              "Open lesson 6: RAW Capture. Walk through enabling Apple ProRAW (Settings → Camera → Formats), Samsung Pro RAW, Pixel RAW+JPEG control. End with the Lightroom Mobile and Snapseed quick-edit workflow.",
              "Scroll to the '10 Practice Projects' block — assign one as homework. Walk through Project 1 (Golden Hour Self-Portrait) so students see the format: GOAL → TECHNIQUE → HOW TO EVALUATE.",
              "End on the 'Ready to Upgrade? Phone vs DSLR' comparison table. Read out the 4 categories where phones still lose: extreme low light, telephoto reach, large-print fine art, shallow DOF. Tell students: 'when ANY of these matters for the shot you want, that's when a DSLR is worth the money'.",
              "Click 'Download as PDF'",
            ],
            pointOut: [
              "The single biggest amateur-to-pro upgrade on a phone is learning AE/AF Lock — tap-and-hold until 'AE/AF Lock' appears in yellow. After 1 day of using it, students never go back.",
              "iPhone Pro models have 2-4 physical lenses. Samsung Ultra and Pixel Pro have 3-4. Switching between them = real optical zoom. Anything beyond the longest physical lens is digital zoom = AVOID.",
              "Portrait Mode breaks on: glasses (halos), fine hair, pet whiskers, busy patterned backgrounds. Teach students to recognize these cases and shoot in regular mode with the telephoto lens instead.",
              "Apple ProRAW is iPhone 12 Pro and newer ONLY. Samsung RAW is in stock Pro mode. Pixel needs the 'Advanced' setting. RAW files are huge (~25 MB) — only enable for important shots.",
              "Lightroom Mobile is FREE for editing JPGs and RAW DNGs — no Adobe subscription needed for basic edits. Snapseed (Google) is also free and excellent. Students never need to pay for mobile editing.",
              "The 10 Practice Projects are intentionally phone-only and progressive — students who finish all 10 have a portfolio of 10 deliberate images, not 1,000 random ones. Encourage a 'one-per-week' rhythm.",
              "Reality check: phones still lose to dedicated cameras in extreme low light, wildlife telephoto, large-print fine art, and any scene where shallow DOF matters more than computational AI can fake.",
            ],
          },
          {
            title: "The Light Meter — Why Pros Still Carry One",
            time: "5 min",
            say: "Even with sophisticated in-camera metering, working pros still bring a handheld light meter to studio sessions. This lesson explains exactly when and why.",
            steps: [
              "Open Learn → Light Meter.",
              "Section 1 — read the 5 scenarios where the camera's built-in meter is fooled (brides in white, black formal wear, snow, studio strobes, mixed ambient + flash). Students see immediately why this matters.",
              "Section 2 — show the visual diagram of INCIDENT vs REFLECTED metering. Point at the white dome (lumisphere) — explain that's what makes a handheld meter different.",
              "Section 3 — read the 'YES use it' and 'NO skip it' columns. Strobes, video, multi-camera = YES. Documentary, sports, snapshots = NO.",
              "Sections 4-5 — walk through the AMBIENT workflow (7 steps) and the FLASH workflow (8 steps). For flash, emphasize: meter button press → 'waiting for flash' state → trigger strobe manually → meter shows aperture.",
              "Section 6 — explain the meter math: ISO + shutter set on meter, meter solves for aperture. Walk the examples.",
              "Section 7 — common mistakes. Especially: dome aimed AT camera, not at the light source.",
              "Section 8 — the 3 buying tiers ($150 / $400 / $700).",
              "Click 'Download as PDF'",
            ],
            pointOut: [
              "The single biggest 'wow' moment for students: 'A bride in white meters at the SAME exposure as a groom in black under the same light, because incident metering measures the LIGHT, not the subject's reflectivity.' That sentence sells the entire tool.",
              "Studio strobe work is the killer use case. Without a meter, you literally CANNOT predict exposure — you test-shot and pray. With a meter, you nail it on shot one.",
              "The meter pays for itself in confidence: paid clients believe you're a wizard when you don't chimp the back of the camera after every shot.",
              "For low-budget students: smartphone apps (Lumu, Pocket Light Meter) work, but accuracy drops fast in mixed light. A real meter is worth saving up for once they shoot any paid work.",
              "Pair the Light Meter lesson with the Lighting and Gray Card lessons — together they're the complete 'controlling light' trilogy.",
            ],
          },
          {
            title: "Camera Mode Dial — Symbol Guide",
            time: "3 min",
            say: "Before students touch settings, they need to know what every symbol on the mode dial actually does. This lesson decodes all of them — universally across Canon, Nikon, Sony, and Pentax.",
            steps: [
              "Open Learn → Camera Mode Dial.",
              "Point at the 4 category cards at the top: Creative Zone (7 modes), Basic Zone (3 modes), Scene Modes (6 modes), Video (1 mode).",
              "Click into the first card — Manual (M). Read out the 5 sections: 'What it does', 'When to use', 'What the camera picks for you' (in this case, nothing), 'Common mistakes', and 'Graduate to'.",
              "Scroll to Aperture Priority (Av) — point out the brand variants line ('Canon: Av · Nikon: A · Sony: A · Pentax: Av'). Tell students: 'whichever brand camera you own, this is the same mode'.",
              "Scroll to the Scene Modes section — explain that the SVG icons (head silhouette, mountains, running figure) match the EXACT icons printed on their mode dial.",
              "Click 'Download as PDF' — students get a printable cheat sheet to keep in their bag.",
            ],
            pointOut: [
              "Most beginners spend YEARS on the green Auto box without ever rotating the dial — show them the Manual card and they realize there's a whole creative zone they've been ignoring.",
              "Every scene mode (Portrait, Sports, Macro, etc.) has a 'Graduate to' suggestion at the bottom — e.g., 'once you understand what Portrait mode does, switch to Av at f/1.8 — same result, full control'. That's the upgrade path.",
              "A-DEP is Canon-only and removed from modern EOS R bodies — if a student has a Canon Rebel, they have it; otherwise, the lesson explicitly says 'won't be on your dial'.",
              "Custom modes (C1/C2/C3 on Canon, U1/U2 on Nikon, 1/2/3 on Sony) are GOLD for working photographers — saved setups recalled with one dial click. Encourage students to configure C1 for their most common scenario.",
              "Video mode is included because more students shoot phone-style auto-video and never realize the 180-degree shutter rule (shutter = 2x frame rate). One slide and they understand why their footage looks juddery.",
            ],
          },
          {
            title: "Posing Guide — 20 Essential Portrait Poses",
            time: "4 min",
            say: "The #1 request from students after a portrait shoot: 'how do I get my subject to STOP looking like a statue?' Today we cover 20 essential poses across 6 categories — every photographer needs these memorized.",
            steps: [
              "Open Learn → Posing Guide",
              "Show the 6-category quick-jump nav at the top: Standing / Seated / Close-Up / Couples / Group / Creative.",
              "Open the Standing section — walk through Contrapposto first (the foundation). Show the AI reference photo + the stick-figure diagram side-by-side. Read the 5-step setup, the angle/lens recommendation, the mistakes (red X marks), and lighting/outfit.",
              "Quickly cycle through S-Curve, Hands-in-Pockets, Lean Against Wall, Walking Step.",
              "Jump to Couples → demonstrate Forehead-to-Forehead pose: 'shoot from the SIDE in profile, not from the front where noses block faces'.",
              "Jump to Group → show the Triangle Composition rule: 'NEVER line everyone up at the same height — vary by sitting some, standing others.'",
              "End on Creative → Backlit Silhouette: 'expose for the bright background, not the model, and choose a pose where the SHAPE alone tells the story.'",
              "Click 'Download as PDF' — students print the full 20-pose reference as a field guide they can carry on shoots.",
            ],
            pointOut: [
              "Models freeze when given no direction. Your job as a photographer is to be a DIRECTOR, not just a button-presser. Memorize these 20 poses and you always have an answer.",
              "The Triangle Group rule alone saves family portrait sessions: NEVER line everyone up flat. Stagger heights. The visual rhythm is everything.",
              "Forehead-to-Forehead from the SIDE is the engagement-photo money shot. Shooting from the front ruins it every time.",
              "Hands are 50% of every portrait. Bad hands = bad pose. Watch for clenched fists, dead-straight hanging arms, and 'where do I put them' awkwardness — give them a job (in pocket, in hair, on hip, holding a prop).",
              "The 'AE/AF Lock + recompose' from Lesson 1 + a memorized pose library = a confident professional shoot, even with non-model subjects.",
            ],
          },
          {
            title: "Camera Maintenance: Sensor Cleaning & Weather Protection",
            time: "2 min",
            say: "Two pieces of expensive equipment ruined by avoidable mistakes: scratched sensors from canned air, and corroded electronics from rain. Today we cover the safety protocols every pro follows.",
            steps: [
              "Open Learn → Maintenance & Care",
              "Read the rose 'DANGER' section — point at the canned compressed air line: 'this is the #1 thing that kills student sensors'",
              "Show the 4 safe tools card (rocket blower → brush → wet swab → professional service)",
              "Read the 7-step sensor cleaning procedure out loud",
              "Scroll to Weather Protection — show the 7-condition protocol table",
              "Stop at 'Beach / sea spray' — emphasize salt corrosion and the Ziploc-bag-before-entering-warm-building rule",
              "Show the long-term Storage section — silica gel, dry cabinets, battery storage at 50%",
              "End on the pro tip: '95% of sensor dust problems are actually smudges on the lens REAR element — check there first'",
              "Click 'Download as PDF'",
            ],
            pointOut: [
              "NEVER use canned compressed air. Propellant + pressure = guaranteed permanent sensor damage. Use a Giottos Rocket Blower ($15) instead.",
              "Modern weather-resistant cameras are NOT weatherPROOF. There's a real difference — read your manual.",
              "Snow + entering a warm building = condensation = corrosion. Always seal the camera in a Ziploc bag BEFORE going inside.",
              "Fungus on lens elements is PERMANENT and costs $200+ to clean. In humid climates, a $200 dry cabinet pays for itself the first lens it saves.",
            ],
          },
          {
            title: "Contracts & Forms (Business Templates)",
            time: "2 min",
            say: "Two pieces of paper separate a hobbyist from a working professional. Today we go to the page that has both ready to download, fill in, and hand to clients or models on the day of the shoot.",
            steps: [
              "Open Learn → Contracts & Forms",
              "Read the rose-bordered LEGAL DISCLAIMER out loud — emphasize these are TEMPLATES, not legal advice. A local attorney must review for jurisdiction.",
              "Stay on the Photography Services Agreement tab. Show the 'When to use a contract' card",
              "Scroll through the full white-paper preview — point at Sections 1 (Parties), 4 (Fees), 5 (Cancellation tiers), 6 (Copyright + AI training opt-out), and 12 (Signatures)",
              "Click 'Download as PDF' — a printable contract is saved to disk",
              "Switch to the Model Release Form tab",
              "Read the 'When to use a model release' card — emphasize the 'editorial generally exempt, commercial ALWAYS needs a release' rule",
              "Scroll the preview — point at Section 4 (Grant of Rights — including AI/synthetic media), Section 7 (Minor / guardian), and Section 9 (Witness line)",
              "Click 'Download as PDF'",
            ],
            pointOut: [
              "Every photographer who has ever been sued tells the same story: 'I didn't have a written contract.' This page eliminates that risk in two clicks.",
              "Section 6 of the contract includes an AI-training opt-out clause — this is a 2026 essential. Without it, your client images could be scraped into training datasets without your control.",
              "For commercial stock sales (Getty, Adobe Stock, Shutterstock): a signed model release is MANDATORY. Editorial use is generally exempt but still risky.",
              "Minors: ALWAYS need parent/guardian signature + government ID number recorded. Section 7 of the release handles this — never skip the ID field.",
              "Always have a witness sign the release — it's the difference between a release that holds up in court and one that doesn't.",
              "Print 10 blank copies of each before every paid shoot. Keep them in your camera bag.",
            ],
          },
        ],
      },
      {
        part: "PART 12: TASK CARDS — PRINTABLE CHEAT SHEETS",
        time: "3 min",
        scenes: [
          {
            title: "The Task Cards Gallery",
            say: "Everything we've covered today also lives as a deck of printable cheat sheets — perfect to hand out or keep in your camera bag.",
            steps: [
              "Click the Task Cards tab in the top navigation",
              "Show the 4 sets: Essentials & Basics, People/Portraiture & Genres, Creative Projects & Composition, and the Bird & Wildlife Pack (46 cards total)",
              "Tap one card (e.g. The Exposure Triangle) → full-screen view with Quick Settings, Key Tips and a 'Remember' takeaway",
              "Click 'Download PDF' on the card, then show 'Download full set' to export a whole set at once",
            ],
            pointOut: [
              "Every card is bilingual (EN/ES) and print-ready with DSLR Advisor branding.",
              "Tell students: print your favorite set and keep it with your gear as a field reference.",
            ],
          },
        ],
      },
      {
        part: "PART 13: LANGUAGE & CLOSING",
        time: "2 min",
        scenes: [
          {
            title: "Cross-Promo: Galería Social",
            time: "0.5 min",
            say: "One last tip — DSLR Advisor teaches you the shot, but you still need somewhere to publish the finished work. We've partnered with Galería Social — a social-gallery app for photographers.",
            steps: [
              "Navigate back to Dashboard (or Sign Out and return to Login)",
              "Point at the small Galería Social card (orange/dark, with the camera icon and CTA)",
              "Tap or click the card → opens galeriasocial.com in a new tab",
              "Encourage students to create a profile and publish their first 5 shots from the Practice Projects",
            ],
            pointOut: [
              "DSLR Advisor + Galería Social = full loop: learn the settings here → take the shot → publish the gallery there.",
              "The card is dismissible per session, so it never gets in the way during class.",
            ],
          },
          {
            title: "Language Toggle & Wrap-Up",
            say: "One more thing — the entire site works in Spanish too.",
            steps: [
              "Click EN button → switches to Spanish instantly",
              "Show menus, results, projects, careers, lighting, glossary, studio — all translated",
              "Switch back to English",
            ],
            pointOut: [
              "CLOSING: 'This app is your starting point. Take these settings, go shoot, check your histogram, and adjust. Save your results, work through the Projects, take the Quiz, plan your studio with the Budget Calculator. The more you use it, the faster you'll learn to shoot manual.'",
              "'The site works on your phone too — take it with you on every shoot.'",
            ],
          },
        ],
      },
    ],
    settingsTable: [
      { scenario: "Golden Hour Portrait", lens: "50mm f/1.8", light: "Golden Hour", type: "Portrait", key: "Wide aperture for blur" },
      { scenario: "Outdoor Sports", lens: "200mm f/5.6", light: "Bright Sun", type: "Sports", key: "Fast shutter (1/1000+)" },
      { scenario: "Dark Church Event", lens: "50mm f/1.8", light: "Low Light", type: "Event Indoor", key: "Max aperture, high ISO" },
      { scenario: "Macro with Tripod", lens: "100mm f/2.8", light: "Cloudy", type: "Macro", key: "f/8-11, low ISO, tripod" },
      { scenario: "Studio Portrait (3-light)", lens: "85mm f/1.8", light: "Studio / Key+Fill+Rim", type: "Portrait", key: "f/5.6, 1/125, ISO 100" },
      { scenario: "Green Screen Chroma Key", lens: "50mm f/1.8", light: "Studio / 2 flat BG lights", type: "Product", key: "f/5.6 or narrower for clean edges" },
    ],
  },

  es: {
    title: "Guion de Presentación",
    subtitle: "DSLR Advisor — Presentación en Clase (~60 min)",
    admin_only: "Solo Administrador — Los estudiantes no pueden ver esta página",
    back: "Volver a la App",
    download: "Descargar como PDF",
    setup_title: "Antes de Empezar (Preparación)",
    toc_label: "Tabla de Contenido",
    setup: [
      "Abre el sitio en una laptop conectada al televisor",
      "Inicia sesión con tu cuenta de antemano para que el equipo este listo",
      "Ten el sitio en el Panel Principal (página principal)",
      "Configura el idioma a Español (o Inglés si tu clase lo prefiere)",
    ],
    say_label: "Decir:",
    do_label: "Hacer:",
    point_out_label: "Senalar:",
    ref_title: "Referencia Rápida — Ajustes de Demo",
    ref_cols: ["Escenario", "Lente", "Luz", "Tipo", "Ajuste Clave"],
    total_time: "Tiempo total de presentación: ~60 minutos",
    script: [
      {
        part: "CONOCIMIENTO DE LA CÁMARA: ANATOMÍA, BAYER E HISTORIA",
        time: "6 min",
        scenes: [
          {
            title: "Anatomía de la Cámara (DSLR vs Mirrorless)",
            say: "Antes de los ajustes, mostremos QUE es la herramienta. Así los ajustes dejan de ser abstractos.",
            steps: [
              "Aprender → Anatomía de la Cámara",
              "Muestra los diagramas SVG: el espejo a 45° y pentaprisma de la DSLR vs el camino directo al sensor de la mirrorless",
              "Recorre los 10 componentes: lente/montura, diafragma, obturador (mecanico/electrónico/global), sensor, procesador, IBIS, OVF vs EVF",
              "Termina en la tabla comparativa honesta DSLR vs mirrorless",
            ],
            pointOut: ["Conecta cada parte con un ajuste que ya enseñaste: el diafragma = apertura, las cortinillas = velocidad."],
          },
          {
            title: "El Filtro Bayer — Como las Cámaras Ven el Color",
            say: "Dato sorprendente: el sensor es daltonico. Mira como reconstruye el color.",
            steps: [
              "Aprender → El Filtro Bayer",
              "Muestra el patrón RGGB y explica por que hay el doble de verde",
              "Explica el demosaicing en 4 pasos y por que el RAW es tan potente",
              "Menciona X-Trans, Foveon y sensores monocromaticos",
            ],
            pointOut: ["Gran momento para justificar disparar en RAW en clase."],
          },
          {
            title: "Una Breve Historia de la Cámara",
            say: "Cerremos el contexto: una historia en 10 partes, de '¿que es la fotografía?' a la IA.",
            steps: [
              "Aprender → Historia de la Cámara",
              "Recorre el temario de 10 puntos en la línea de tiempo: (1) que es la fotografía, (2) cámara oscura y como funciona, (3) la primera foto permanente de Niépce, (4) Daguerre y el Daguerrotipo, (5) el Calotipo de Fox Talbot vs el Daguerrotipo, (6) colodion húmedo / placas secas / película y rollos, (7) Eastman y Kodak, (8) color: Autochrome y Kodachrome, (9) digital: CCD vs CMOS, (10) la actualidad: DSLR, mirrorless, smartphones e IA",
              "Señala el #5 — explica POR QUE el negativo de Talbot supero al positivo único de Daguerre (reproducibilidad = el futuro)",
              "Descarga el PDF como material de lectura",
            ],
            pointOut: ["Mensaje de cierre: el equipo cambia, el ver no."],
          },
          {
            title: "Blanco y Negro + Fotografía Análoga (Opcional)",
            say: "Dos lecciones de oficio que profundizan COMO ven los estudiantes — perfectas para una clase más avanzada.",
            steps: [
              "Aprender → Fotografía en Blanco y Negro: por que disparar en B/N, rango tonal + bases del Sistema de Zonas, contraste/sombras/altas luces como tema, filtros de contraste, cuando el color ayuda vs estorba, y convertir vs B/N nativo",
              "Demo de entrecerrar: muestra como entrecerrar colapsa la escena en masas tonales — exactamente como se lee una imagen B/N",
              "Aprender → Fotografía Análoga (35mm): el renacer analógico, películas, carga y medición, Sunny 16, revelado",
              "Baja a la nueva sección 'Fallo de Reciprocidad y Largas Exposiciones' — explica por que una exposición nocturna medida necesita MAS tiempo",
              "Ambas lecciones se descargan como material PDF bilingüe",
            ],
            pointOut: ["Refuerza: el film y el B/N obedecen el mismo triángulo de exposición — solo quitan la red de seguridad de la pantalla."],
          },
        ],
      },
      {
        part: "PARTE 1: EL ASESOR",
        time: "10 min",
        scenes: [
          {
            title: "Escena 1 — Retrato en Hora Dorada",
            time: "3 min",
            say: "Imaginen que están afuera durante la hora dorada y quieren tomar un retrato de un amigo. Les voy a mostrar lo que la app recomienda.",
            steps: [
              "En el panel de Relleno Rápido, clic en Canon EOS Rebel T3i (se resalta en dorado)",
              "Clic en tu lente 50mm f/1.8 (auto-rellena distancia focal y apertura)",
              "Tipo de Iluminación → Natural",
              "Condición de Luz → Hora Dorada",
              "Hora del Día → Atardecer",
              "Tipo de Foto → Retrato",
              "Trípode APAGADO, Movimiento → Quieto, Profundidad → Poca",
              "Clic en 'Calcular Ajustes'",
            ],
            pointOut: [
              "Los retratos priorizan la apertura primero — apertura más abierta = desenfoque de fondo cremoso",
              "El indicador de zona ISO esta verde = limpio, sin ruido",
              "Tres alternativas: Equilibrado, Más Seguro, Acción — diferentes compromisos",
              "Bajar a 'Desde Tu Equipo' — elige el mejor lente de TU inventario",
            ],
          },
          {
            title: "Escena 2 — Congelar Acción Deportiva",
            time: "3 min",
            say: "Ahora están en un partido de basketball bajo el sol brillante. Un escenario totalmente diferente.",
            steps: [
              "Mantener T3i seleccionada",
              "Distancia Focal → 200, Apertura Máxima → 5.6 (lente teleobjetivo)",
              "Tipo de Lente → Teleobjetivo",
              "Iluminación → Natural, Condición de Luz → Sol Brillante",
              "Hora del Día → Mediodía",
              "Tipo de Foto → Deportes",
              "Movimiento → Rápido",
              "Clic en 'Calcular Ajustes'",
            ],
            pointOut: [
              "La velocidad de obturador subio a 1/1000+ — el modo Deportes congela el movimiento",
              "El ISO puede ser más alto — revisen el indicador amarillo (algo de ruido)",
              "Advertencia: 'considere un lente más rápido' — f/5.6 deja entrar menos luz",
              "Mostrar la alternativa Acción — velocidad aún más rápida para momentos cumbre",
            ],
          },
          {
            title: "Escena 3 — Evento en Iglesia Oscura",
            time: "2 min",
            say: "El escenario difícil — dentro de una iglesia oscura para una ceremonia. No se permite flash.",
            steps: [
              "Cambiar a 50mm f/1.8, lente Estándar",
              "Condición de Luz → Poca Luz",
              "Hora del Día → Noche",
              "Tipo de Foto → Evento Interior",
              "Escenario de Evento → Iglesia",
              "Clic en 'Calcular Ajustes'",
            ],
            pointOut: [
              "Apertura completamente abierta (f/1.8), ISO alto — indicador rojo = ruidoso",
              "Advertencia especifica de la cámara sobre el límite de ISO de la T3i",
              "Bajar a la Guía de Evento: sin flash, usar luz disponible, consejos para iglesia",
            ],
          },
          {
            title: "Escena 4 — Macro con Trípode",
            time: "2 min",
            say: "Probemos un acercamiento extremo de una flor.",
            steps: [
              "Distancia Focal → 100, Apertura → 2.8",
              "Tipo de Lente → Macro",
              "Condición de Luz → Nublado",
              "Tipo de Foto → Macro",
              "Trípode → ENCENDIDO",
              "Movimiento → Quieto, Profundidad → Mucha",
              "Clic en 'Calcular Ajustes'",
            ],
            pointOut: [
              "La profundidad de campo en macro es mínima — la app sugiere f/8-f/11 para nitidez",
              "Trípode ENCENDIDO = velocidad más lenta, ISO más bajo = imagen más limpia",
              "Advertencia de macro sobre distancia mínima de enfoque — consejo práctico real",
            ],
          },
        ],
      },
      {
        part: "IA: DESCRIBE TU TOMA",
        time: "3 min",
        scenes: [
          {
            title: "Modo IA — Describe tu Toma",
            say: "Para principiantes que aún no conocen la jerga, la IA traduce una frase normal en ajustes manuales.",
            steps: [
              "Arriba de la calculadora, cambia el interruptor de 'Manual' a 'Describe tu Toma'",
              "Escribe una toma en lenguaje sencillo (ej. 'partido de futbol con movimiento rápido en una tarde soleada') o toca un ejemplo",
              "Clic en 'Obtener Ajustes IA' — muestra Apertura, Velocidad, ISO, el escenario, el objetivo recomendado y consejos pro",
              "Senala la insignia 'De tu equipo': la IA eligio el mejor objetivo que el estudiante posee en Mi Equipo",
              "Clic en 'Aplicar al Formulario Manual' — la IA rellena la calculadora Manual; ahora ajusta, Calcula y Guarda en el Historial",
            ],
            pointOut: [
              "Mensaje clave: la IA es el puente del lenguaje cotidiano al control manual — no un reemplazo del aprendizaje.",
            ],
          },
        ],
      },
      {
        part: "GUARDAR Y DESCARGAR",
        time: "1 min",
        scenes: [
          {
            title: "Guardar Resultados y Descargar PDF",
            say: "Cada cálculo se puede guardar y descargar como hoja de trabajo.",
            steps: [
              "Clic en 'Guardar al Historial' (icono de marcador en resultados)",
              "Navegar a la pestana de Historial",
              "Clic en la flecha para expandir la entrada guardada",
              "Clic en el botón de descarga PDF",
              "Mostrar el PDF — página blanca limpia, todos los ajustes, listo para imprimir",
            ],
            pointOut: [],
          },
        ],
      },
      {
        part: "PARTE 2: MI INVENTARIO DE EQUIPO",
        time: "3 min",
        scenes: [
          {
            title: "Mostrar Inventario de Equipo",
            say: "La app te permite guardar todo tu equipo para recomendaciones personalizadas.",
            steps: [
              "Clic en 'Mi Equipo' en la barra de navegación",
              "Mostrar la cámara guardada (Canon T3i) y lente (50mm f/1.8)",
              "Senalar los campos de detalle: modelo, serial, condición, precio",
              "Mostrar secciones de Iluminación (speedlight) y Soporte (trípode)",
              "Abrir la categoría Iluminación — señalar las filas dedicadas a Fotómetros (Sekonic L-308X) y Checkers de Color (Datacolor SpyderCHECKR, X-Rite ColorChecker). 'Todo lo que toca color y exposición vive en una sola pestaña.'",
              "Baja a la nueva sección 'Cámaras de Película' — agrega un cuerpo analógico como la Canon AE-1 Program. 'Los cuerpos de película no tienen EXIF digital, pero igual puedes inventariarlos aquí junto a tu equipo digital.'",
              "Clic en 'Descargar Excel' — exporta el inventario como hoja de cálculo",
            ],
            pointOut: [],
          },
        ],
      },
      {
        part: "PARTE 3: HERRAMIENTAS DE FOTOGRAFÍA",
        time: "5 min",
        scenes: [
          {
            title: "Recorrido de Herramientas y Fundamentos",
            say: "La pestana de Herramientas es tu salón de fotografía en una caja.",
            steps: [
              "Clic en 'Herramientas' en la barra de navegación",
              "Triángulo de Exposición — mostrar los 3 pilares",
              "Calculadora de Profundidad — cambiar apertura de 2.8 a 11, mostrar cambio",
              "Velocidad Mínima a Mano — ingresar distancia focal para velocidad segura",
              "Referencia Sunny 16 — reglas clásicas de exposición",
              "Ayudante Sunny-16 / Fotómetro — elige escena + ISO para obtener apertura y velocidad (ideal para película o un fotómetro muerto)",
              "Calculadora de Fallo de Reciprocidad — ingresa un tiempo medido (ej. 8s) + película para obtener la larga exposición corregida (~15s en Ilford)",
              "Lector EXIF — elige la foto de un estudiante y revela los ajustes con que fue tomada (se lee solo en el navegador, nunca se sube); luego toca 'Usar en el Calculador' para compararla con la recomendación de la app",
              "Bajar a Fundamentos:",
              "  • Regla de los Tercios — clic en intersecciones para ver consejos",
              "  • Temperatura de Color — mostrar escala Kelvin de cálido a frío",
              "  • Composición — 6 tarjetas de técnicas",
              "  • Dominio Técnico — modos de enfoque, medición, balance de blancos",
              "  • Post-Procesado — 6 pasos de recorte a exportar",
            ],
            pointOut: [],
          },
          {
            title: "Quiz de Fotografía",
            say: "¿Quién quiere poner a prueba sus conocimientos?",
            steps: [
              "Clic en 'Comenzar Quiz'",
              "Responder 2-3 preguntas en vivo con la clase",
              "Mostrar explicación después de cada respuesta",
              "12 preguntas en total — los estudiantes lo intentan solos",
            ],
            pointOut: [],
          },
          {
            title: "Cuestionarios por Lección (Aprender → Cuestionarios)",
            say: "Cada lección ahora termina con un cuestionario corto — veamos qué aprendimos.",
            steps: [
              "Bajar al final de la lección que acabas de dar — 'Pon a prueba lo aprendido' (5 preguntas)",
              "Responder una pregunta juntos; mostrar la retroalimentación verde/roja y la explicación",
              "Abrir Aprender → Cuestionarios para mostrar los 27 cuestionarios en un solo lugar",
              "Los estudiantes terminan solos y comparan puntajes (solo los 5/5 perfectos se guardan, como insignias)",
              "Un 5/5 perfecto gana una insignia — aparece una medalla verde junto a esa lección en el menú Aprender",
              "Tocar 'Imprimir (PDF)' para descargar una hoja para la clase con una página aparte de Clave de Respuestas",
              "Abrir Aprender → Examen Final: 30 preguntas al azar, aprueba con 80% para un certificado descargable y compartible",
              "Tras el examen, mostrar 'Tus puntos débiles': las lecciones de cada pregunta fallada, con las respuestas correctas y un botón 'Hacer su cuestionario'",
              "Tocar 'Practicar solo mis falladas' para una ronda rápida — las incorrectas se repiten hasta acertar todas, luego volver a presentar el examen",
              "Senalar la insignia azul 'Racha de Práctica ×N' y la tarjeta del Campeón Mensual (trofeo dorado + imagen para publicar)",
              "Profesor: en 'Progreso de Estudiantes' revisar 'Puntos débiles de la clase' para decidir que repasar, y 'Descargar CSV' para calificaciones",
              "Profesor: crear una clase en 'Progreso de Estudiantes' → Clases, compartir su código; filtrar progreso, puntos débiles y CSV por clase, e 'Imprimir cuestionario de repaso' con las 10 preguntas más falladas",
              "Mostrar el Salón de la Fama desde la tarjeta del Campeón Mensual",
              "Profesor: Presenter → 'Progreso de Estudiantes' muestra el avance de cada estudiante en los proyectos",
              "Mostrar la Tabla de la Clase en Aprender → Cuestionarios — los estudiantes la activan con 'Mostrarme como …' (solo nombre + inicial)",
              "Tras aprobar el examen, 'Imagen para publicar (1080×1080)' da un certificado cuadrado para Galería Social / Instagram",
            ],
            pointOut: ["Las opciones se mezclan en cada intento — 'Intentar de nuevo' da un orden nuevo"],
          },
        ],
      },
      {
        part: "PARTE 4: PROYECTOS",
        time: "3 min",
        scenes: [
          {
            title: "Proyectos Fotográficos",
            say: "Listos para practicar? Estas son sus asignaciones prácticas.",
            steps: [
              "Clic en 'Proyectos' en la barra de navegación",
              "Expandir Fotografía de Alimentos — Principiante. Mostrar objetivo, ajustes, consejos",
              "Expandir Deportes y Acción — Avanzado. Velocidad rápida, modo ráfaga",
              "Expandir Noche y Larga Exposición — trípode, ISO bajo, exposiciones de 30 segundos",
              "Senalar los indicadores de dificultad: Verde=Principiante, Dorado=Intermedio, Rojo=Avanzado",
              "Abrir un proyecto y mostrar el nuevo Reto, luego marcar una casilla de la Lista de Entregables — el progreso se guarda en la cuenta del estudiante (ver el contador y el resumen 'Tu progreso' arriba)",
              "Bajar a 'Entrega tu trabajo en Galería Social' — los estudiantes publican ellos mismos sus fotos terminadas en galeriasocial.com para recibir comentarios e inspiración",
              "Senalar el botón 'Compartir en Galería Social' junto a Ajustes Sugeridos (también aparece en los resultados del Calculador)",
            ],
            pointOut: [],
          },
        ],
      },
      {
        part: "PARTE 5: CARRERAS FOTOGRAFICAS",
        time: "2 min",
        scenes: [
          {
            title: "Caminos Profesionales",
            say: "La fotografía no es solo un hobby — es una carrera profesional.",
            steps: [
              "Clic en 'Carreras' en la barra de navegación",
              "Expandir Fotógrafo de Bodas — mostrar trabajo, habilidades, salario",
              "Expandir Fotógrafo Deportivo — 'Si les encanta la acción, este es su camino'",
              "Expandir Fotógrafo Forense — 'Apuesto a que no sabian que esto existia'",
              "20 carreras en total — explorar en su propio tiempo",
            ],
            pointOut: [],
          },
        ],
      },
      {
        part: "PARTE 6: TÉCNICAS DE ILUMINACIÓN",
        time: "3 min",
        scenes: [
          {
            title: "Patrones de Iluminación para Retrato",
            say: "Los grandes fotógrafos no solo presionan el botón — moldean la luz. Veamos 10 patrones clásicos de iluminación.",
            steps: [
              "Clic en 'Iluminación' en la barra de navegación",
              "Expandir 'Rembrandt' — senalar el triángulo de luz en la mejilla",
              "Expandir 'Butterfly / Paramount' — iluminación glamour clásica de Hollywood",
              "Expandir 'Split' — mitad sombra, mitad luz, animo dramático",
              "Expandir 'Rim / Contraluz' — separa al sujeto del fondo",
              "Bajar a 'Kits de Luz' — 8 tipos de equipo explicados (speedlights a luz natural de ventana)",
              "Clic en 'Descargar como PDF' — folleto listo para clase",
            ],
            pointOut: [
              "Cada patrón muestra: descripción, ángulo del sujeto, posición de la luz principal, consejos de montaje",
              "Referencia perfecta para practicar en casa — domina uno, pasa al siguiente",
              "Full EN/ES — útil para compañeros bilingües",
            ],
          },
        ],
      },
      {
        part: "PARTE 7: HORA DORADA Y TIEMPOS SOLARES",
        time: "3 min",
        scenes: [
          {
            title: "Planea tu Sesión Alrededor de la Luz Mágica",
            say: "Los fotógrafos profesionales no disparan en cualquier momento — disparan cuando la luz es mejor. Esta página hace los calculos por ti.",
            steps: [
              "Haz clic en 'Hora Dorada' en la barra de navegación",
              "La primera vez: toca 'Usar mi ubicación' — la página detecta tu ciudad automáticamente",
              "O usa la pestana 'Manual' y busca 'Madrid' o cualquier otra ciudad",
              "Senala las cuatro tarjetas de 'Mejores Ventanas para Disparar' — Hora Azul Matutina, Hora Dorada Matutina, Hora Dorada Vespertina, Hora Azul Vespertina",
              "Cada tarjeta muestra el rango de tiempo, duración y un consejo rápido — esa es tu lista de disparo del día",
              "Desplázate a la Línea de Tiempo Solar completa — 13 fases con consejos del fotógrafo en cada una",
              "Muestra las Estadisticas Rápidas — horas de amanecer + atardecer con direcciones (E/W y grados)",
              "Toca las flechas o el selector de fecha para planear una sesión mañana o el próximo fin de semana",
              "Desplázate a la sección Luna — salida, puesta, fase actual, porcentaje de iluminación",
            ],
            pointOut: [
              "Todos los tiempos se calculan localmente en tu teléfono — no requiere internet una vez cargada",
              "Las direcciones del compas te dicen exactamente donde saldra y se pondra el sol — perfecto para explorar",
              "Úsalo la noche antes de una sesión para programar tu alarma para la hora dorada",
              "La sección de luna es un bono para fotografía nocturna y planificación de astrofotografía",
            ],
          },
        ],
      },
      {
        part: "PARTE 8: GLOSARIO FOTOGRAFICO",
        time: "2 min",
        scenes: [
          {
            title: "Glosario Bilingüe de Fotografía",
            say: "Si olvidas un término, no tienes que abrir un libro — solo escribes.",
            steps: [
              "Clic en 'Glosario' en la barra de navegación",
              "Escribir 'apertura' en la búsqueda — filtro en vivo",
              "Revisar algunas entradas para mostrar definiciones en Inglés + Español lado a lado",
              "46+ términos cubriendo exposición, lentes, iluminación, composición, post-producción",
              "Clic en 'Descargar como PDF' — glosario completo para estudio sin conexión",
            ],
            pointOut: [
              "Excelente para estudiantes cuyo primer idioma no es el Inglés",
              "Funciona durante una sesión — busca en el teléfono entre tomas",
            ],
          },
        ],
      },
      {
        part: "PARTE 9: MONTAJE DE ESTUDIO Y CALCULADORA",
        time: "5 min",
        scenes: [
          {
            title: "Guía de Montaje de Estudio",
            say: "Quiza la página más valiosa si suenas con tu propio estudio. Todo lo que necesitas para montarlo — pequeño o mediano.",
            steps: [
              "Clic en 'Estudio' en la barra de navegación",
              "Pasar la Calculadora de Presupuesto (ya regresaremos) a Planeación del Espacio",
              "Bajar por las 9 secciones: Espacio, Kit de Iluminación (con fotos), Disparadores, Fotómetros, Modificadores, Fondos y Pantalla Verde, Equipo de Soporte",
              "Senalar el bloque de 4 Consejos de Pantalla Verde — distancia del sujeto, luz plana, advertencias de ropa",
              "Bajar a los 2 diagramas SVG — Pequeño (3 luces) y Mediano (5 luces)",
              "Mostrar las Listas de Inicio — Mínimo Viable (~$1,200) y Mediano (~$4,000+)",
            ],
            pointOut: [
              "Todas las tarjetas de equipo tienen fotos de referencia reales",
              "Los diagramas etiquetan luces Key / Fill / Rim / Fondo más posiciones de Sujeto y Cámara",
              "Descargar PDF da un documento completo de planeación de estudio",
            ],
          },
          {
            title: "Calculadora de Presupuesto — Demo en Vivo",
            say: "Ahora la función estrella — le dices a la app lo que puedes gastar, y te dice exactamente que comprar primero.",
            steps: [
              "Regresar al inicio de Estudio, a la Calculadora de Presupuesto",
              "Arrastrar el control a $500 → nivel 'Estudio Inicial', aparecen 8 artículos en verde",
              "Arrastrar a $1,500 → 'Estudio Pequeño', caben 17 artículos, $1,485 usados",
              "Arrastrar a $4,000 → 'Estudio Mediano', caben 31 artículos",
              "Arrastrar a $8,000 → 'Estudio Avanzado', caben 40 artículos",
              "Arrastrar a $12,000 → 'Kit Pro Completo', todos los 43 caben, aparece el banner verde 'kit completo'",
              "El consejo debajo del control cambia con cada nivel — consejo personalizado",
            ],
            pointOut: [
              "43 artículos ordenados por prioridad — cada dólar va al siguiente equipo de mayor impacto",
              "La lista 'Ahorra para lo Siguiente' muestra lo que viene, así siempre sabes que sigue",
              "El PDF captura tu presupuesto + nivel + listas actuales — compartelo con la familia cuando pidas equipo!",
            ],
          },
          {
            title: "Lista de Compras (Usuarios con Sesión)",
            say: "Un truco más — si ya tienes parte del equipo, la calculadora se adapta.",
            steps: [
              "Marcar la casilla junto a un artículo que 'ya tienes' (ej. Speedlight #1)",
              "Ver como el artículo se mueve a 'Ya Tienes' con texto tachado",
              "Notar que el siguiente artículo sube a 'Dentro de tu Presupuesto'",
              "El encabezado muestra 'Tienes: 3 / 43 ($425 ahorrado)'",
              "Cerrar sesión y volver a entrar — la lista persiste entre dispositivos",
            ],
            pointOut: [
              "La calculadora excluye los artículos que ya tienes, así tu presupuesto rinde más",
              "Guardado en la nube por usuario — funciona en teléfono, tablet, escritorio",
              "Excelente para planeación continua — regresas en 3 meses después de comprar, el plan se actualiza solo",
            ],
          },
        ],
      },
      {
        part: "PARTE 10: IMPRESION Y SALIDA",
        time: "3 min",
        scenes: [
          {
            title: "De Archivo Digital a Impresión",
            say: "Las grandes fotos merecen salir de la pantalla. Esta página es el puente entre un archivo RAW y una impresión enmarcada en la pared.",
            steps: [
              "Clic en 'Impresión' en la barra",
              "Mostrar la tabla de referencia — 4x6 hasta poster 24x36, con píxeles requeridos a 300 / 240 / 180 DPI",
              "Abrir la Calculadora de Impresión — ingresar dimensiones (ej. 6000 x 4000)",
              "Senalar los 3 resultados: tamaños max. a 300, 240 y 180 DPI",
              "Bajar a los diagramas de proporciones — mostrar como 8x10 pierde ~17% de un cuadro 3:2",
              "Mostrar las 8 tarjetas del Flujo de Procesado Digital",
              "Bajar a Enmarcado y Matting + Gestion de Color + Lista para el Laboratorio",
              "Clic en 'Descargar como PDF' — referencia completa para el laboratorio",
            ],
            pointOut: [
              "Estudiantes con cámaras de menos MP ven rápido su techo de impresión",
              "Los compañeros pueden planear la composición pensando en el recorte — no más cabezas cortadas a 8x10",
              "La Lista para el Laboratorio es una protección de una página contra reimpresiones ($$ ahorrado)",
              "Full EN/ES — útil para trabajar con labs en Español",
            ],
          },
        ],
      },
      {
        part: "PARTE 11: TÉCNICAS AVANZADAS (Histograma, Medición, Compensación EV, Factor Recorte, Planos, Calibración, Tether, Filtros, Compresión, Enfoque, BB/RD, Tarjeta Gris, Smartphone, Poses, Mantenimiento, Contratos)",
        time: "41 min",
        time: "16 min",
        scenes: [
          {
            title: "El Histograma — herramienta fundamental de exposición",
            time: "3 min",
            say: "Antes de cualquier otro tema técnico, todo estudiante necesita hacerse amigo del histograma. La LCD miente — el histograma no. Funciona igual en Canon, Nikon, Sony y Fuji porque lee el sensor, no la marca.",
            steps: [
              "Abrir Aprender → El Histograma en el menú desplegable",
              "Mostrar el Mapa Tonal de 5 zonas en la parte superior — Punto Negro, Sombras, Medios Tonos, Altas Luces, Punto Blanco",
              "Bajar a la alerta roja 'El Muro' y leer la frase 'LOS DATOS QUE TOCAN EL BORDE SE PIERDEN PARA SIEMPRE' en voz alta",
              "Mostrar la nota 'no existe la curva perfecta en forma de campana'",
              "Recorrer los 3 ejemplos de escena (Low Key / High Key / Alto Contraste) — para cada uno: señalar la foto, luego el histograma, luego leer el tip",
              "Mostrar la sección de Blinkies y la Guía Universal de Hardware (el botón DISP/INFO funciona en cualquier marca)",
              "Clic en 'Descargar como PDF' para el folleto del aula",
            ],
            pointOut: [
              "Lo más importante: NO hay forma 'buena' ni 'mala' del histograma — solo una que coincide con el animo de la escena",
              "Si tienes que recortar algo, recorta las sombras. El detalle perdido en sombras parece 'atmósfera'; las altas luces quemadas parecen un error",
              "Dile a los estudiantes que dediquen 5 minutos UNA vez en el menú de su cámara para activar el histograma en Live View Y en Reproducción — lo usaran en cada disparo a partir de entonces",
              "Los Blinkies (Alerta de Altas Luces) te dicen DONDE en la imagen ocurre el clipping que el histograma reporta — usalos juntos",
            ],
          },
          {
            title: "Modos de Medición — como la cámara mide la luz",
            time: "3 min",
            say: "El histograma muestra el resultado. Los Modos de Medición deciden COMO la cámara produce ese resultado. Misma lección para usuarios de Canon, Nikon, Sony y Fuji.",
            steps: [
              "Abrir Aprender → Modos de Medición",
              "Mostrar los 4 diagramas — Matrix (todo el encuadre encendido), Promedio Central (elipse brillante), Parcial (punto mediano), Spot (punto diminuto)",
              "Senalar el mapeo de nombres por marca: Matrix = Nikon, Evaluativa = Canon, Multi-segmento = Sony/Fuji",
              "Leer el cuadro del Flujo Profesional: elegir modo → medio presionar → revisar histograma → aplicar EV → disparar",
              "Mostrar las rutas de botón para Canon/Nikon/Sony/Fuji",
              "Clic en 'Descargar como PDF'",
            ],
            pointOut: [
              "El 80% de profesionales usa Matrix/Evaluativa + Compensación de Exposición — casi nunca cambian de modo",
              "Spot es la respuesta cuando Matrix falla — conciertos, rostros a contraluz, la luna",
              "En la mayoría de los cuerpos, Spot sigue al punto AF activo. Si el AF esta en el cielo, estas midiendo el cielo, no a tu sujeto",
              "Resetea a Matrix entre sesiones — dejar Spot activado es la causa #1 de exposiciones misteriosas a la mañana siguiente",
            ],
          },
          {
            title: "Compensación de Exposición — el override +/-",
            time: "4 min",
            say: "El mejor tema de la app para corregir exposición al vuelo. El medidor cree que toda foto debe promediar gris al 18% — lo cual arruina la nieve, arruina la noche, arruina los retratos a contraluz. La Compensación es el override.",
            steps: [
              "Abrir Aprender → Compensación de Exposición",
              "Leer 'El Por que' — el problema del 18% gris con 4 ejemplos (la nieve se vuelve gris, el tuxedo negro se vuelve gris, el rostro a contraluz queda en silueta, el primer plano del atardecer se oscurece)",
              "Bajar a la sección del SLIDER INTERACTIVO — este es el highlight",
              "Arrastrar el slider de 0 → +2 EV. La vista derecha se aclara VISIBLEMENTE mientras la izquierda se mantiene. Leer el valor EV en vivo.",
              "Arrastrar el slider de +2 → −2 EV. La vista derecha se oscurece. Pausar y dejar que los estudiantes comparen.",
              "Mostrar el mini histograma debajo del slider — se desplaza izquierda/derecha al arrastrar. Esto ES lo que ocurre dentro de la cámara.",
              "Bajar a la grilla de 8 escenarios reales — leer la fila de nieve (+1 a +1.7 EV) y la fila de concierto (−1 a −2 EV)",
              "Mostrar las rutas de botón (Canon, Nikon, Sony, Fuji) y la tabla de compatibilidad con modos automáticos",
              "Clic en 'Descargar como PDF' para el folleto del aula",
            ],
            pointOut: [
              "Si el histograma se acumula contra el muro IZQUIERDO → suma +EV. Si se acumula contra el muro DERECHO → resta −EV. Es la misma lección que acabas de dar en la sección del Histograma.",
              "La mayoría de los principiantes sub-compensa. Diles: +2 EV en nieve es normal, no extremo.",
              "Las Sony / Fuji mirrorless muestran una previsualización EN VIVO que se actualiza al girar el dial — ven como quedará la foto antes de disparar",
              "Resetea EV a 0 cuando cambie la luz o pases a una escena nueva — si no, subexpondras por accidente la siguiente foto familiar",
            ],
          },
          {
            title: "Distancia Focal y Factor de Recorte",
            time: "2.5 min",
            say: "Un lente de 50mm NO es un 50mm en toda cámara. El tamaño de sensor lo cambia todo — ángulo de visión, profundidad de campo, que equipo deberías comprar.",
            steps: [
              "Abrir Aprender → Factor de Recorte",
              "Mostrar el diagrama SVG de comparación de sensores — 6 rectangulos concentricos, todos centrados, todos a escala",
              "Senalar: Full Frame = base (1.0x), APS-C Canon (1.6x) es ligeramente más pequeño que APS-C Nikon/Sony/Fuji (1.5x), Micro 4/3 es 2.0x",
              "Mostrar la tabla de factor de recorte — un lente de 50mm 'se convierte' en 80mm en APS-C Canon, 75mm en APS-C Nikon, 100mm en Micro 4/3",
              "Bajar a las tarjetas de Pros/Cons — sensores chicos = más baratos + 'alcance' gratis para vida silvestre; sensores grandes = mejor gran angular + mejor poca luz + PDC reducida",
              "Mostrar el mapa de denominaciones: RF/RF-S (Canon), FX/DX (Nikon), FE/E (Sony), XF/GF (Fuji)",
              "Clic en 'Descargar como PDF' para la referencia de compra",
            ],
            pointOut: [
              "Un 85mm f/1.8 en Full Frame es el 'look de retrato' clásico. Para igualarlo en APS-C necesitarias un 56mm f/1.2 — que existe pero cuesta más",
              "Los fotógrafos de deportes/vida silvestre suelen PREFERIR APS-C porque el factor de recorte les da 'alcance gratis' (un 300mm se comporta como 450mm)",
              "NO persigas full-frame porque las revistas lo dicen. Los cuerpos APS-C modernos producen imágenes profesionales hermosas y ahorran miles en lentes",
              "Si pones un lente 'full-frame' en un cuerpo APS-C, funciona bien (solo recorta). Si pones un lente 'solo APS-C' en un cuerpo full-frame, obtienes esquinas negras (vinieteado).",
            ],
          },
          {
            title: "Tipos de Planos — el lenguaje visual",
            time: "1.5 min",
            say: "Ahora: todo fotógrafo necesita hablar el lenguaje del encuadre — la misma persona se lee totalmente distinta en primer plano que en plano general.",
            steps: [
              "Abrir Aprender → Tipos de Planos en el menú desplegable",
              "Recorrer las 8 tarjetas, de arriba a abajo",
              "Detenerse en Plano Medio — mostrar el recuadro de recorte del diagrama frente a la foto IA",
              "Mostrar los 3 tips pro en un par de tarjetas (elección de lente, errores comunes de recorte)",
              "Clic en 'Descargar como PDF'",
            ],
            pointOut: [
              "8 planos estándar en orden cinematográfico — primerisimo plano hasta gran plano general",
              "El recuadro amarillo punteado en cada diagrama = exactamente lo que captura la cámara",
              "Foto + diagrama lado a lado enseña tanto el encuadre abstracto COMO la imagen real",
              "El folleto PDF funciona como asignación: fotografiar un ejemplo de cada plano en una sola sesión",
            ],
          },
          {
            title: "Calibración del Monitor — Pruebas en Vivo",
            time: "2 min",
            say: "Si tu pantalla miente, tus impresiones también. Esta página enseña calibración Y te deja probar tu propio monitor ahora mismo.",
            steps: [
              "Abrir Aprender → Calibración del Monitor",
              "Repasar rápido las secciones Por qué / Espacios de Color / Objetivos",
              "Bajar a 'Pruebas en Vivo del Monitor'",
              "Correr las 6 pruebas en la pantalla del aula: nivel de negro, nivel de blanco, rampa de grises, gamma, uniformidad de color, carta de nitidez",
              "Leer la instrucción debajo de cada prueba en voz alta — los estudiantes auto-diagnostican",
              "Pedir a algunos estudiantes que abran la página en sus teléfonos y corran las mismas pruebas",
            ],
            pointOut: [
              "Estas pruebas usan solo CSS estándar — sin plugins, sin descargas, funcionan en cualquier pantalla",
              "Bandas en la rampa de grises = limitaciones de panel de 6 bits en monitores baratos",
              "La TV del aula casi siempre falla el test 'la fila de gamma 2.2 se mezcla mejor' — gran demo real",
              "Los estudiantes con laptop pueden recalibrar en casa con las herramientas integradas del sistema",
            ],
          },
          {
            title: "Fotografía con Tether",
            time: "1.5 min",
            say: "Si alguna vez disparas para un cliente, aprende a usar tether. Estudio, producto, moda, demos en aula — un cable a la laptop lo cambia todo.",
            steps: [
              "Abrir Aprender → Fotografía con Tether",
              "Mostrar las tarjetas verde Ventajas / rojo Desventajas",
              "Bajar a la Comparativa de software — senalar Capture One (el estándar de estudio) y las herramientas gratuitas oficiales (Canon EOS Utility, Nikon NX Tether)",
              "Bajar a la sección de Cables — enfatizar el ancla JerkStopper",
              "Mostrar la lista de 8 pasos para empezar",
              "Clic en 'Descargar como PDF' para la referencia de estudio",
            ],
            pointOut: [
              "Compra UN solo cable Tether Tools de calidad ($25-70) — se paga solo la primera vez que no falla en plena sesión",
              "Si alguna vez tiras una cámara de $1500 al tropezar con un cable, el JerkStopper es lo que te hubiera salvado",
              "Capture One Express es GRATIS para usuarios Fuji — senalarlo",
              "Para aulas: hacer tether de la cámara del instructor así todos los estudiantes ven en tiempo real lo que ve el lente",
            ],
          },
          {
            title: "Filtros de Hardware: UV, CPL, ND",
            time: "2.5 min",
            say: "La edición moderna puede arreglar exposición, color y ruido — pero NO PUEDE eliminar un reflejo, profundizar un cielo lavado, ni congelar el movimiento del agua. Tres filtros cubren casi toda situación.",
            steps: [
              "Abrir Aprender → Filtros de Hardware",
              "Mostrar la sección UV — enfatizar que para sensores digitales esto es puramente PROTECCIÓN ($30-50 de vidrio sacrificial)",
              "Bajar al slider Antes/Después del CPL. ARRASTRARLO lentamente — cielo lavado a la izquierda se vuelve azul profundo a la derecha, los reflejos del lago desaparecen revelando rocas debajo",
              "Bajar al slider Antes/Después del ND. ARRASTRARLO — cascada congelada a la izquierda se vuelve sedosa a la derecha",
              "Mostrar la tarjeta de las 5 densidades ND (ND2 / ND8 / ND64 / ND1000 / Variable)",
              "Clic en 'Descargar como PDF'",
            ],
            pointOut: [
              "Los filtros UV baratos DEGRADAN la calidad (flare/ghosting) — compra B+W o Hoya HD multi-coated, o omite el UV",
              "El Polarizador Circular es el UN filtro que todo fotógrafo debe tener. NO se puede replicar en software.",
              "El ND64 (6 pasos) es el más usado — comprado este ANTES del 10 pasos",
              "Los ND variables son comodos pero los baratos producen un patrón en X en los extremos — evita los de $20",
              "Compra UN filtro del tamaño de tu lente MAS GRANDE, y usa anillos step-up — ahorra dinero a largo plazo",
            ],
          },
          {
            title: "Compresión de Lente — Distancia, NO Distancia Focal",
            time: "2 min",
            say: "Esta es la mentira más repetida en tutoriales de fotografía: '200mm comprime el fondo'. Falso. El LENTE magnifica. La DISTANCIA cámara-sujeto crea la perspectiva. Hoy terminamos este mito para siempre.",
            steps: [
              "Abrir Aprender → Compresión de Lente",
              "Leer en voz alta la tarjeta rosa 'Mito', luego la verde 'Verdad'",
              "Bajar al slider Antes/Después — ARRASTRARLO. Izquierda: 24mm a 1m, montañas pequeñas. Derecha: 200mm a 30m, MISMA persona, montañas ENORMES detrás",
              "Pausar. Dejar que la clase internalice: 'el lente no hizo nada — los PIES del fotógrafo si'",
              "Mostrar la lista 'Como lo usan los pros' — enfatizar las reglas de retrato y fotos grupales",
              "Clic en 'Descargar como PDF'",
            ],
            pointOut: [
              "Los grandes angulares distorsionan rostros a corta distancia — esto es GEOMETRÍA, no defecto del lente. Retrocede, cambia a 85mm, problema resuelto.",
              "Regla de foto grupal: NUNCA fotografies un grupo con un 24mm de cerca. Los del borde se ven gordos. Retrocede, usa 50-85mm.",
              "Si quieres que las montañas se vean ENORMES detrás de tu sujeto: retrocede LEJOS y usa teleobjetivo. La magia es la distancia, no el vidrio.",
              "Lightroom puede arreglar distorsión del lente (barril/cojin) pero NO la distorsión de perspectiva. La única solución es mover los pies la próxima vez.",
            ],
          },
          {
            title: "Enfoque Avanzado: Back-Button Focus y AI Eye-AF",
            time: "3 min",
            say: "Dos técnicas separan aficionados de profesionales: desacoplar el enfoque del disparador, y aprovechar el rastreo de ojos por IA. Se configura una vez, se usa para siempre.",
            steps: [
              "Abrir Aprender → Enfoque Avanzado",
              "Explicar BBF en palabras simples: 'Ahora mismo, medio presionar el disparador = enfoque. Vamos a mover eso al botón AF-ON atrás, así el disparador SOLO dispara.'",
              "Leer los puntos 'Por qué los pros lo hacen' — enfocar y recomponer, rastrear ráfagas sin re-enfocar, eliminar shutter lag",
              "Mostrar las tarjetas de configuración por marca (Canon / Nikon Z / Sony / Fujifilm) — elige la marca del estudiante y recorrela en vivo",
              "Bajar a AI Eye-AF — explicarlo como 'la cámara encuentra el ojo automáticamente y lo rastrea, incluso a f/1.4 con PDC delgadisima'",
              "Mostrar las tarjetas de Eye-AF por marca",
              "Terminar en la tarjeta verde del Combo — 'BBF + Eye-AF juntos es lo que usan todos los pros modernos de boda/retrato'",
              "Clic en 'Descargar como PDF'",
            ],
            pointOut: [
              "La primera semana de BBF se siente raro. Tras 1 semana, los estudiantes no vuelven atrás. La memoria muscular es permanente.",
              "Eye-AF funciona con lentes, rostros parcialmente cubiertos y mascarillas — pero NO con ojos cerrados. Para recien nacidos durmiendo, usa AF de punto único en la ceja",
              "Fotógrafos de deportes/vida silvestre: activen Animal Eye AF o Bird Eye AF — son modos separados en cuerpos modernos",
              "Para Sony A7 IV+, el overlay 'Subject Recognition Frame' muestra EN VIVO que ojo esta rastreando la cámara — señalarlo como demo",
            ],
          },
          {
            title: "Balance de Blancos y Rango Dinámico",
            time: "2 min",
            say: "Dos fundamentos técnicos que deciden si tus fotos tienen tonos de piel naturales, cielos quemados o sombras embarradas. La escala Kelvin y el techo de RD de tu sensor.",
            steps: [
              "Abrir Aprender → Balance de Blancos y RD",
              "Mostrar la franja de gradiente Kelvin — naranja cálido (1700K vela) a la izquierda hasta azul frío (10,000K cielo) a la derecha",
              "Leer el cuadro de la REGLA DE ORO: 'K BAJO para combatir luz CÁLIDA, K ALTO para combatir luz FRÍA' — es contraintuitivo y confunde a la mayoría",
              "Mostrar la tarjeta de las 5 estrategias WB (Auto / Presets / Custom / K Manual / RAW)",
              "Bajar a Rango Dinámico — leer el dato 'full-frame = 14-15 pasos, ojos = 20+'",
              "Mostrar las instrucciones de la prueba de RD en casa en 5 pasos — los estudiantes pueden hacerlo esta noche con su cámara",
              "Mostrar la sección Bracketing y HDR — demostrar el flujo",
              "Terminar en la tarjeta naranja Conexión con el Histograma — enlace directo a la lección del histograma",
              "Clic en 'Descargar como PDF'",
            ],
            pointOut: [
              "Regla de vida: dispara RAW + Auto WB para el 95% de situaciones. Para el otro 5%, invierte en un X-Rite ColorChecker ($100)",
              "Para VIDEO: bloquea el Kelvin Manual una vez al inicio de la toma, no lo cambies durante la toma — los cambios de color clip-a-clip arruinan la edición",
              "Cuando una escena excede el RD de tu sensor (atardecer detrás de montañas), haz bracket-y-mezcla — no pelees contra la física, abraza el HDR",
              "Photonstophotos.net tiene una base de datos GRATIS del RD de toda cámara moderna — los estudiantes deberian buscar su propio modelo",
            ],
          },
          {
            title: "La Tarjeta Gris: Precisión de Color y Exposición",
            time: "3 min",
            say: "Hoy cubrimos la herramienta más barata y más subutilizada en fotografía profesional — un pedazo de cartón de $18 que resuelve cuatro problemas que ningún software puede arreglar: precisión del fotómetro en escenas de alto contraste, balance de blancos bajo CUALQUIER luz, sincronización de color entre múltiples cámaras, y verificación de calibración de enfoque.",
            steps: [
              "Abrir Aprender → La Tarjeta Gris",
              "Mostrar el visual de 3 baldosas: negro / gris 18% / blanco — explicar que cada fotómetro en la Tierra esta calibrado para ese tono medio exacto",
              "Bajar a 'Vencer la Trampa del Fotómetro' — recorrer los 4 ejemplos: novia de blanco, gato negro, paisaje nevado, silueta a contraluz. Enfatizar la TRAMPA (rosa) y la SOLUCIÓN (esmeralda) de cada caso.",
              "Leer el flujo de 5 pasos Medición Puntual + AE-Lock",
              "Bajar a 'Maestria en Balance de Blancos Custom' — recorrer el procedimiento de 6 pasos en cámara, luego señalar las 4 tarjetas por género (producto / retrato / lifestyle / bienes raices)",
              "Bajar a 'Sincronización de Color Multi-Cámara' — explicar el escenario moderno (DSLR + iPhone + dron) y recorrer el flujo de 6 pasos en Lightroom",
              "Bajar a 'Manejo de Superficies Brillantes y Reflectantes' — enfatizar la detección de derrame de color (sofá rojo en botella blanca de perfume = contaminación rosa)",
              "Bajar a 'Verificación de Enfoque y Nitidez' — señalar las tarjetas de marca AFMA/AF Fine Tune/AF Micro Adj",
              "Bajar a 'Subir de Nivel: El Color Checker de 24 Parches (Datacolor SpyderCHECKR 24)' — señalar el diseño visual de 24 parches. Explicar que es la tarjeta gris mejorada con 18 parches de color calibrados incorporados.",
              "Leer la lista de CUANDO usarlo — enfatizar que NO es para cada sesión. Solo producto, retrato de estudio, reproducción de bellas artes, matching de video y trabajos con color exacto.",
              "Recorrer el flujo de CAMPO de 5 pasos: foto de referencia al inicio de cada montaje de luz, etiquetar el archivo, re-disparar cuando cambia la iluminación.",
              "Recorrer el flujo de LIGHTROOM de 7 pasos: balance de blancos en el parche gris, exportar DNG, arrastrar al software SpyderCHECKR, generar perfil DCP personalizado, aplicar perfil a la referencia, sincronizar con todas las tomas.",
              "Terminar en el Tip Pro naranja: '95% de las fotos suaves a f/11 son problema de ENFOQUE, no de difracción'",
              "Clic en 'Descargar como PDF'",
            ],
            pointOut: [
              "La tarjeta gris es la diferencia entre un aficionado y un profesional. Los pros la sacan en los primeros 30 segundos de una sesión. Los aficionados nunca poseen una.",
              "Para producto/comercial de e-commerce, el WB custom desde tarjeta es INNEGOCIABLE. El Auto WB arruina la precisión de color en playeras blancas, comida, y cualquier empaque de marca.",
              "La sincronización multi-cámara es el superpoder 2026: los teléfonos ahora capturan stills competentes, los drones dan aereas, y los clientes esperan todo color-matched. Un frame de tarjeta por cámara = edición unificada.",
              "Para novias y nieve: mide sobre tarjeta en la MISMA luz, bloquea la exposición con AE-Lock, después dispara. Esta sola técnica salva más tomas que todos los sliders de Lightroom combinados.",
              "El SpyderCHECKR 24 es la ruta de mejora natural desde una tarjeta gris básica. Dile a los estudiantes: la tarjeta gris vive en la mochila, el SpyderCHECKR vive en el kit de estudio. Lleva ambos — resuelven problemas diferentes.",
              "El software gratis SpyderCHECKR (incluido con la tarjeta) crea perfiles DCP personalizados para Lightroom. Los estudiantes que disparan productos o reproducción de bellas artes deberian comprar un SpyderCHECKR (~$45-50) antes que cualquier lente nuevo.",
            ],
          },
          {
            title: "Fotografía con Smartphone",
            time: "4 min",
            say: "Los estudiantes de hoy alcanzan su teléfono antes que una DSLR. Los teléfonos modernos tope de gama disparan 12-50 MP con HDR computacional que rivaliza con cámaras dedicadas para trabajo diario. Esta lección les enseña a DEJAR de ser aficionados con su teléfono.",
            steps: [
              "Abrir Aprender → Fotografía con Smartphone",
              "Señalar la sub-nav de salto rápido arriba — explicar que permite a los estudiantes saltar directamente a cualquiera de las 6 lecciones",
              "Abrir lección 1: Dominar la Exposición y el Enfoque Móvil. Recorrer la columna iOS (cuadrado amarillo + slider de sol + bloqueo AE/AF) y la columna Android (enfoque + slider de brillo + toca-y-mantén) lado a lado.",
              "Abrir lección 2: Modo Pro. Explicar que iOS necesita una app de terceros (Halide, Lightroom Mobile) mientras Samsung/OnePlus tienen modo Pro integrado y Pixel necesita Open Camera.",
              "Abrir lección 3: Reglas de Composición. Demostrar como activar las líneas de cuadrícula 3x3 en iOS (Ajustes → Cámara → Cuadrícula) y en la cámara stock Android.",
              "Abrir lección 4: Selección de Lentes. Sacar un teléfono en vivo — mostrar los botones de lentes fisicos .5x, 1x, 2x/3x. Martillar la regla: 'nunca pellizques más allá del lente físico más largo — eso es basura por software'.",
              "Abrir lección 5: Modo Retrato. Demostrar el punto dulce de distancia 60cm-2.4m, los estilos de iluminación de iOS, y el ajuste retroactivo de bokeh de Pixel en Editar → Herramientas → Desenfoque.",
              "Abrir lección 6: Captura RAW. Recorrer la activación de Apple ProRAW (Ajustes → Cámara → Formatos), Samsung Pro RAW, control RAW+JPEG en Pixel. Terminar con el flujo rápido de edición en Lightroom Mobile y Snapseed.",
              "Bajar al bloque '10 Proyectos Prácticos' — asignar uno como tarea. Recorrer el Proyecto 1 (Autorretrato Hora Dorada) para que los estudiantes vean el formato: OBJETIVO → TÉCNICA → COMO EVALUAR.",
              "Terminar en la tabla 'Listo para Subir de Nivel? Teléfono vs DSLR'. Leer las 4 categorías donde los teléfonos aún pierden: baja luz extrema, alcance telefoto, bellas artes en impresión grande, PDC poca. Decir a los estudiantes: 'cuando CUALQUIERA de estas importa para la toma que quieres, ahi vale la pena una DSLR'.",
              "Clic en 'Descargar como PDF'",
            ],
            pointOut: [
              "La mejora amateur-a-pro más grande en un teléfono es aprender el bloqueo AE/AF — toca-y-mantén hasta que aparezca 'Bloqueo AE/AF' en amarillo. Tras 1 día de uso, los estudiantes no vuelven atrás.",
              "Los modelos iPhone Pro tienen 2-4 lentes fisicos. Samsung Ultra y Pixel Pro tienen 3-4. Cambiar entre ellos = zoom óptico real. Cualquier cosa más allá del lente físico más largo es zoom digital = EVITAR.",
              "El Modo Retrato falla con: lentes (halos), pelo fino, bigotes de mascotas, fondos con patrones ocupados. Enseña a los estudiantes a reconocer estos casos y disparar en modo normal con el telefoto.",
              "Apple ProRAW es SOLO iPhone 12 Pro y posteriores. Samsung RAW esta en el modo Pro stock. Pixel necesita el ajuste 'Avanzado'. Los archivos RAW son enormes (~25 MB) — solo activalos para tomas importantes.",
              "Lightroom Mobile es GRATIS para editar JPGs y RAW DNGs — no necesitas suscripción Adobe para edición básica. Snapseed (Google) también es gratis y excelente. Los estudiantes nunca necesitan pagar por edición móvil.",
              "Los 10 Proyectos Prácticos son intencionalmente solo-teléfono y progresivos — los estudiantes que terminan los 10 tienen un portafolio de 10 imágenes deliberadas, no 1000 al azar. Fomenta un ritmo de 'uno por semana'.",
              "Reality check: los teléfonos aún pierden contra cámaras dedicadas en baja luz extrema, telefoto de fauna silvestre, bellas artes en impresión grande, y cualquier escena donde la PDC poca profunda importe más de lo que la IA computacional puede falsificar.",
            ],
          },
          {
            title: "El Fotómetro — Por Que los Pros Aún Lo Cargan",
            time: "5 min",
            say: "Incluso con la medición sofisticada de la cámara, los profesionales en activo siguen llevando un fotómetro de mano a las sesiones de estudio. Esta lección explica exactamente cuando y por que.",
            steps: [
              "Abrir Aprender → Fotómetro.",
              "Sección 1 — leer los 5 escenarios donde el fotómetro integrado se deja engañar (novia de blanco, ropa negra formal, nieve, strobes de estudio, ambiente mixto + flash). Los estudiantes ven al instante por que importa.",
              "Sección 2 — mostrar el diagrama visual de medición INCIDENTE vs REFLEJADA. Señalar la cupula blanca (lumisfera) — explicar que eso es lo que hace diferente al fotómetro de mano.",
              "Sección 3 — leer las columnas 'SI uselo' y 'NO saltelo'. Strobes, video, multi-cámara = SI. Documental, deportes, snapshots = NO.",
              "Secciones 4-5 — recorrer el flujo AMBIENTE (7 pasos) y el flujo FLASH (8 pasos). Para flash, enfatizar: presionar botón del fotómetro → estado 'esperando flash' → disparar strobe manualmente → fotómetro muestra apertura.",
              "Sección 6 — explicar las matematicas del fotómetro: ISO + obturación en el fotómetro, el fotómetro resuelve la apertura. Recorrer los ejemplos.",
              "Sección 7 — errores comunes. Especialmente: cupula apuntando A la cámara, NO a la fuente de luz.",
              "Sección 8 — los 3 niveles de compra ($150 / $400 / $700).",
              "Clic en 'Descargar como PDF'",
            ],
            pointOut: [
              "El momento de 'wow' más grande para los estudiantes: 'Una novia de blanco mide a la MISMA exposición que un novio de negro bajo la misma luz, porque la medición incidente mide la LUZ, no la reflectividad del sujeto.' Esa oración vende la herramienta entera.",
              "Trabajo de strobes de estudio es el caso de uso definitivo. Sin fotómetro, literalmente NO PUEDES predecir exposición — disparas de prueba y rezas. Con fotómetro, lo clavas en la toma uno.",
              "El fotómetro se paga solo en confianza: los clientes pagados creen que eres un mago cuando no revisas la pantalla trasera tras cada disparo.",
              "Para estudiantes de bajo presupuesto: las apps de smartphone (Lumu, Pocket Light Meter) funcionan, pero la precisión baja rápido en luz mixta. Un fotómetro real vale la pena ahorrarlo apenas hagan trabajo pagado.",
              "Combina la lección de Fotómetro con las lecciones de Iluminación y Tarjeta Gris — juntas son la trilogia completa de 'controlando la luz'.",
            ],
          },
          {
            title: "Dial de Modos de la Cámara — Guía de Símbolos",
            time: "3 min",
            say: "Antes de que los estudiantes toquen ajustes, necesitan saber que hace cada símbolo del dial de modos. Esta lección los decodifica todos — universal para Canon, Nikon, Sony y Pentax.",
            steps: [
              "Abrir Aprender → Dial de Modos.",
              "Señalar las 4 tarjetas de categoría arriba: Zona Creativa (7 modos), Zona Básica (3 modos), Modos de Escena (6 modos), Video (1 modo).",
              "Entrar a la primera tarjeta — Manual (M). Leer las 5 secciones: 'Qué hace', 'Cuándo usarlo', 'Lo que la cámara elige por ti' (en este caso, nada), 'Errores comunes' y 'Subir de nivel a'.",
              "Bajar a Prioridad de Apertura (Av) — señalar la línea de variantes por marca ('Canon: Av · Nikon: A · Sony: A · Pentax: Av'). Decir a los estudiantes: 'cualquier marca de cámara que tengas, es el mismo modo'.",
              "Bajar a la sección Modos de Escena — explicar que los iconos SVG (silueta de cabeza, montañas, figura corriendo) coinciden con los iconos EXACTOS impresos en su dial.",
              "Clic en 'Descargar como PDF' — los estudiantes obtienen una hoja imprimible para llevar en la mochila.",
            ],
            pointOut: [
              "La mayoría de principiantes pasan AÑOS en el cuadro verde Auto sin rotar el dial — muestrales la tarjeta Manual y se darán cuenta de que hay toda una zona creativa que han estado ignorando.",
              "Cada modo de escena (Retrato, Deportes, Macro, etc.) tiene una sugerencia 'Subir de nivel a' al final — ej. 'cuando entiendes que hace el modo Retrato, cambia a Av a f/1.8 — mismo resultado, control total'. Esa es la ruta de mejora.",
              "A-DEP es solo Canon y eliminado en cuerpos EOS R modernos — si un estudiante tiene una Canon Rebel, lo tiene; de lo contrario, la lección dice explicitamente 'no estara en tu dial'.",
              "Los modos personalizados (C1/C2/C3 en Canon, U1/U2 en Nikon, 1/2/3 en Sony) son ORO para fotógrafos en activo — configuraciones guardadas se llaman con un clic del dial. Anima a los estudiantes a configurar C1 para su escenario más común.",
              "Se incluyo el modo Video porque más estudiantes graban video estilo teléfono en auto y nunca se dan cuenta de la regla de 180 grados (obturador = 2x frame rate). Una diapositiva y entienden por que su material se ve con saltos.",
            ],
          },
          {
            title: "Guía de Poses — 20 Poses Esenciales de Retrato",
            time: "4 min",
            say: "La petición #1 de los estudiantes después de una sesión de retrato: 'como hago que mi sujeto DEJE de parecer una estatua?' Hoy cubrimos 20 poses esenciales en 6 categorías — cada fotógrafo necesita memorizarlas.",
            steps: [
              "Abrir Aprender → Guía de Poses",
              "Mostrar el sub-nav de salto rápido por 6 categorías arriba: De Pie / Sentado/a / Primer Plano / Parejas / Grupos / Creativo.",
              "Abrir la sección De Pie — recorrer Contrapposto primero (la base). Mostrar la foto de referencia IA + el diagrama lado a lado. Leer los 5 pasos, recomendación de ángulo/lente, errores (X rojas) e iluminación/vestuario.",
              "Pasar rápido por Curva en S, Manos en Bolsillos, Apoyado en Pared, Paso Caminando.",
              "Saltar a Parejas → demostrar la pose Frente con Frente: 'dispara desde el LADO en perfil, no de frente donde las narices bloquean los rostros'.",
              "Saltar a Grupos → mostrar la regla de Composición en Triángulo: 'NUNCA los alinies a la misma altura — varia sentando a unos, parando a otros.'",
              "Terminar en Creativo → Silueta a Contraluz: 'expón para el fondo brillante, no el modelo, y elige una pose donde la FORMA cuente la historia sola.'",
              "Clic en 'Descargar como PDF' — los estudiantes imprimen la referencia completa de 20 poses como guía de campo.",
            ],
            pointOut: [
              "Los modelos se congelan sin dirección. Tu trabajo como fotógrafo es ser DIRECTOR, no solo aprietabotones. Memoriza estas 20 poses y siempre tendrás respuesta.",
              "La regla del Grupo en Triángulo sola salva sesiones familiares: NUNCA los alinies planos. Escalona alturas. El ritmo visual es todo.",
              "Frente con Frente desde el LADO es la toma estrella de fotos de compromiso. Disparar de frente la arruina cada vez.",
              "Las manos son el 50% de cada retrato. Manos malas = pose mala. Cuida los puños cerrados, brazos colgando rectos, y la incomodidad de 'donde las pongo' — dales un trabajo (bolsillo, cabello, cadera, sosteniendo prop).",
              "El 'Bloqueo AE/AF + recompon' de la Lección 1 + una biblioteca mental de poses = una sesión profesional segura, incluso con sujetos no-modelos.",
            ],
          },
          {
            title: "Mantenimiento de Cámara: Limpieza de Sensor y Protección Climatica",
            time: "2 min",
            say: "Dos piezas de equipo caro arruinadas por errores evitables: sensores rayados por aire en lata, y electrónica corroida por lluvia. Hoy cubrimos los protocolos de seguridad que sigue todo profesional.",
            steps: [
              "Abrir Aprender → Mantenimiento y Cuidado",
              "Leer la sección rosa 'PELIGRO' — señalar la línea del aire comprimido en lata: 'esto es lo #1 que mata sensores de estudiantes'",
              "Mostrar la tarjeta de las 4 herramientas seguras (soplador rocket → cepillo → hisopo húmedo → servicio profesional)",
              "Leer el procedimiento de 7 pasos de limpieza de sensor en voz alta",
              "Bajar a Protección Climatica — mostrar la tabla de protocolos de 7 condiciones",
              "Detenerse en 'Playa / brisa marina' — enfatizar la corrosión por sal y la regla de la bolsa Ziploc-antes-de-entrar-a-edificio-cálido",
              "Mostrar la sección de Almacenamiento a largo plazo — silica gel, gabinetes secos, baterías al 50%",
              "Terminar en el tip pro: 'el 95% de los problemas de polvo en sensor son en realidad manchas en el elemento TRASERO del lente — revisa ahi primero'",
              "Clic en 'Descargar como PDF'",
            ],
            pointOut: [
              "NUNCA uses aire comprimido en lata. Propelente + presión = daño permanente al sensor garantizado. Usa un Soplador Giottos Rocket ($15) en su lugar.",
              "Las cámaras modernas resistentes al clima NO son impermeables. Hay una diferencia real — lee tu manual.",
              "Nieve + entrar a edificio cálido = condensación = corrosión. Sella siempre la cámara en una bolsa Ziploc ANTES de entrar.",
              "El hongo en elementos de lente es PERMANENTE y cuesta $200+ limpiarlo. En climas humedos, un gabinete seco de $200 se paga solo con el primer lente que salva.",
            ],
          },
          {
            title: "Contratos y Formularios (Plantillas de Negocio)",
            time: "2 min",
            say: "Dos hojas de papel separan a un aficionado de un profesional. Hoy vamos a la página que tiene ambas listas para descargar, llenar y entregar a clientes o modelos el día de la sesión.",
            steps: [
              "Abrir Aprender → Contratos y Formularios",
              "Leer en voz alta el AVISO LEGAL con borde rosa — enfatizar que son PLANTILLAS, no asesoría legal. Un abogado local debe revisarlas para la jurisdicción.",
              "Permanecer en la pestaña Acuerdo de Servicios Fotográficos. Mostrar la tarjeta 'Cuándo usar un contrato'",
              "Recorrer la vista previa completa en blanco — señalar las Secciones 1 (Partes), 4 (Honorarios), 5 (Niveles de cancelación), 6 (Derechos de autor + opt-out de entrenamiento de IA) y 12 (Firmas)",
              "Clic en 'Descargar como PDF' — un contrato imprimible se guarda en disco",
              "Cambiar a la pestaña Cesión de Imagen / Model Release",
              "Leer la tarjeta 'Cuándo usar una cesión de imagen' — enfatizar la regla 'editorial generalmente exento, comercial SIEMPRE necesita cesión'",
              "Recorrer la vista previa — señalar la Sección 4 (Cesión de Derechos — incluyendo medios sinteticos/IA), Sección 7 (Menor / tutor) y Sección 9 (Línea de testigo)",
              "Clic en 'Descargar como PDF'",
            ],
            pointOut: [
              "Todo fotógrafo que ha sido demandado cuenta la misma historia: 'no tenia contrato escrito'. Esta página elimina ese riesgo en dos clics.",
              "La Sección 6 del contrato incluye una clausula de opt-out de entrenamiento de IA — esto es esencial en 2026. Sin ella, las imágenes de tus clientes pueden ser raspadas a datasets de entrenamiento sin tu control.",
              "Para ventas de stock comercial (Getty, Adobe Stock, Shutterstock): una cesión de imagen firmada es OBLIGATORIA. El uso editorial generalmente está exento pero igual es arriesgado.",
              "Menores: SIEMPRE necesitan firma de padre/tutor + número de ID oficial registrado. La Sección 7 de la cesión maneja esto — nunca te saltes el campo de ID.",
              "Haz que siempre firme un testigo la cesión — es la diferencia entre una cesión que aguanta en corte y una que no.",
              "Imprime 10 copias en blanco de cada una antes de cada sesión pagada. Tenlas en tu bolso de cámara.",
            ],
          },
        ],
      },
      {
        part: "PARTE 12: TASK CARDS — HOJAS DE REFERENCIA IMPRIMIBLES",
        time: "3 min",
        scenes: [
          {
            title: "La Galería de Task Cards",
            say: "Todo lo que vimos hoy también existe como un mazo de hojas de referencia imprimibles — ideales para repartir o guardar en tu bolso de cámara.",
            steps: [
              "Haz clic en la pestana Task Cards en la navegación superior",
              "Muestra los 4 sets: Esenciales y Fundamentos, Personas/Retrato y Generos, Proyectos Creativos y Composición, y el Pack de Aves y Fauna (46 tarjetas en total)",
              "Toca una tarjeta (ej. El Triángulo de Exposición) → vista a pantalla completa con Ajustes Rápidos, Consejos Clave y una idea para 'Recordar'",
              "Haz clic en 'Descargar PDF' en la tarjeta, luego muestra 'Descargar set completo' para exportar todo un set de una vez",
            ],
            pointOut: [
              "Cada tarjeta es bilingüe (EN/ES) y está lista para imprimir con la marca DSLR Advisor.",
              "Dile a los estudiantes: imprime tu set favorito y tenlo con tu equipo como referencia de campo.",
            ],
          },
        ],
      },
      {
        part: "PARTE 13: IDIOMA Y CIERRE",
        time: "2 min",
        scenes: [
          {
            title: "Cross-Promo: Galería Social",
            time: "0.5 min",
            say: "Un último tip — DSLR Advisor les enseña la toma, pero aún necesitan donde publicar el trabajo terminado. Nos asociamos con Galería Social — una app de galería social para fotógrafos.",
            steps: [
              "Navegar de vuelta al Panel Principal (o Cerrar Sesión y volver a Inicio de Sesión)",
              "Señalar la pequeña tarjeta de Galería Social (naranja/oscura, con el icono de cámara y el CTA)",
              "Tocar o hacer clic en la tarjeta → abre galeriasocial.com en una pestaña nueva",
              "Animar a los estudiantes a crear un perfil y publicar sus primeras 5 tomas de los Proyectos Prácticos",
            ],
            pointOut: [
              "DSLR Advisor + Galería Social = ciclo completo: aprende los ajustes aquí → toma la foto → publica la galería allá.",
              "La tarjeta se puede descartar por sesión, así que nunca estorba durante la clase.",
            ],
          },
          {
            title: "Cambio de Idioma y Cierre",
            say: "Una cosa más — todo el sitio funciona en inglés también.",
            steps: [
              "Clic en el botón ES → cambia a Inglés instantáneamente",
              "Mostrar menus, resultados, proyectos, carreras, iluminación, glosario, estudio — todo traducido",
              "Cambiar de vuelta a Español",
            ],
            pointOut: [
              "CIERRE: 'Esta app es su punto de partida. Tomen estos ajustes, salgan a disparar, revisen su histograma y ajusten. Guarden sus resultados, trabajen en los Proyectos, tomen el Quiz, planeen su estudio con la Calculadora. Mientras más la usen, más rápido aprenderan a disparar en manual.'",
              "'El sitio funciona en su teléfono también — llevenlo a cada sesión de fotos.'",
            ],
          },
        ],
      },
    ],
    settingsTable: [
      { scenario: "Retrato Hora Dorada", lens: "50mm f/1.8", light: "Hora Dorada", type: "Retrato", key: "Apertura abierta para desenfoque" },
      { scenario: "Deportes al Aire Libre", lens: "200mm f/5.6", light: "Sol Brillante", type: "Deportes", key: "Velocidad rápida (1/1000+)" },
      { scenario: "Evento en Iglesia Oscura", lens: "50mm f/1.8", light: "Poca Luz", type: "Evento Interior", key: "Apertura máxima, ISO alto" },
      { scenario: "Macro con Trípode", lens: "100mm f/2.8", light: "Nublado", type: "Macro", key: "f/8-11, ISO bajo, trípode" },
      { scenario: "Retrato en Estudio (3 luces)", lens: "85mm f/1.8", light: "Estudio / Key+Fill+Rim", type: "Retrato", key: "f/5.6, 1/125, ISO 100" },
      { scenario: "Pantalla Verde Chroma Key", lens: "50mm f/1.8", light: "Estudio / 2 luces planas en fondo", type: "Producto", key: "f/5.6 o más cerrado para bordes limpios" },
    ],
  },
};

export default function PresenterPage() {
  const { user } = useAuth();
  const { lang, t } = useTranslation();
  const printRef = useRef(null);
  const [exportingUsers, setExportingUsers] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  const c = content[lang] || content.en;

  useEffect(() => {
    if (!user || user === false || user.role !== "admin") return;
    axios
      .get("/api/analytics/pageviews", { withCredentials: true })
      .then(({ data }) => setAnalytics(data))
      .catch(() => setAnalytics({ rows: [], total_views: 0, total_students: 0 }))
      .finally(() => setAnalyticsLoading(false));
  }, [user]);

  // Map lesson path -> translated label for the engagement table.
  const lessonLabel = (path) => {
    const item = LEARNING_PATH.find((l) => l.to === path);
    return item ? t(item.labelKey) : path;
  };

  const { ready, generating, download } = usePDFExport(
    printRef,
    () => `dslr-advisor-presenter-script-${lang}.pdf`,
    [lang]
  );

  const handleExportUsers = async () => {
    setExportingUsers(true);
    try {
      const token = localStorage.getItem("dslr_access_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users-csv`,
        {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "dslradvisor-users.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[Users CSV export] failed:", err);
      alert(lang === "es" ? "Error al exportar usuarios. Revisa la consola." : "Failed to export users. Check console for details.");
    } finally {
      setExportingUsers(false);
    }
  };

  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <>
      <div className="print:hidden max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2 flex items-center justify-between">
        <Link to="/">
          <Button variant="ghost" className="text-[#A1A1AA] hover:text-white hover:bg-white/5" data-testid="presenter-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            {c.back}
          </Button>
        </Link>
        <Button onClick={download} disabled={!ready} className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60" data-testid="presenter-print-btn">
          <Printer className="w-4 h-4 mr-1.5" />
          {generating ? (lang === "es" ? "Generando..." : "Generating...") : c.download}
        </Button>
      </div>

      <div className="print:hidden max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-4 flex items-center justify-end gap-2 flex-wrap">
        <Button
          variant="outline"
          onClick={handleExportUsers}
          disabled={exportingUsers}
          className="border-[#F5A524]/30 bg-[#F5A524]/5 text-[#F5A524] hover:bg-[#F5A524]/10 hover:text-[#F5B041] disabled:opacity-60"
          data-testid="presenter-export-users-btn"
        >
          <DownloadSimple weight="fill" className="w-4 h-4 mr-1.5" />
          {exportingUsers
            ? lang === "es" ? "Exportando..." : "Exporting..."
            : lang === "es" ? "Exportar Usuarios (CSV)" : "Export Users (CSV)"}
        </Button>
        <Link to="/teacher-progress">
          <Button
            variant="outline"
            className="border-[#F5A524]/30 bg-[#F5A524]/5 text-[#F5A524] hover:bg-[#F5A524]/10 hover:text-[#F5B041]"
            data-testid="presenter-teacher-progress-btn"
          >
            <ChalkboardTeacher weight="fill" className="w-4 h-4 mr-1.5" />
            {lang === "es" ? "Progreso de Estudiantes" : "Teacher Progress View"}
          </Button>
        </Link>
        <Link to="/qr-poster">
          <Button
            variant="outline"
            className="border-[#F5A524]/30 bg-[#F5A524]/5 text-[#F5A524] hover:bg-[#F5A524]/10 hover:text-[#F5B041]"
            data-testid="presenter-qr-poster-btn"
          >
            <QrCode weight="fill" className="w-4 h-4 mr-1.5" />
            {lang === "es"
              ? "Poster QR para el Aula (PDF)"
              : "Classroom QR Poster (PDF)"}
          </Button>
        </Link>
      </div>

      {/* Lesson Engagement — admin analytics (not part of the printable script) */}
      <div className="print:hidden max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-6" data-testid="lesson-engagement">
        <div className="bg-[#121214]/80 border border-white/10 rounded-xl p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-1">
            <ChartBar weight="fill" className="w-5 h-5 text-[#F5A524]" />
            <h2 className="font-['Outfit'] text-white text-lg font-semibold">
              {lang === "es" ? "Participación en Lecciones" : "Lesson Engagement"}
            </h2>
          </div>
          <p className="text-[#71717A] text-sm mb-4">
            {lang === "es"
              ? "Vistas de páginas de lección por estudiantes (no incluye vistas de admin)."
              : "Lesson pageviews by students (admin views are excluded)."}
          </p>

          {analyticsLoading ? (
            <p className="text-[#71717A] text-sm" data-testid="engagement-loading">
              {lang === "es" ? "Cargando..." : "Loading..."}
            </p>
          ) : !analytics || analytics.rows.length === 0 ? (
            <p className="text-[#71717A] text-sm" data-testid="engagement-empty">
              {lang === "es"
                ? "Aún no hay datos. Apareceran cuando los estudiantes visiten las lecciones."
                : "No data yet. Stats appear once students start visiting lessons."}
            </p>
          ) : (
            <>
              <div className="flex gap-6 mb-4">
                <div data-testid="engagement-total-views">
                  <p className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
                    {lang === "es" ? "Vistas Totales" : "Total Views"}
                  </p>
                  <p className="font-['JetBrains_Mono'] text-2xl text-white">{analytics.total_views}</p>
                </div>
                <div data-testid="engagement-total-students">
                  <p className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
                    {lang === "es" ? "Estudiantes" : "Students"}
                  </p>
                  <p className="font-['JetBrains_Mono'] text-2xl text-white">{analytics.total_students}</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[#71717A] text-xs uppercase tracking-[0.1em] border-b border-white/10">
                      <th className="text-left font-medium py-2">{lang === "es" ? "Lección" : "Lesson"}</th>
                      <th className="text-right font-medium py-2 w-24">{lang === "es" ? "Vistas" : "Views"}</th>
                      <th className="text-right font-medium py-2 w-28">{lang === "es" ? "Estudiantes" : "Students"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.rows.map((r) => (
                      <tr key={r.path} className="border-b border-white/5" data-testid={`engagement-row-${r.path.replace(/\//g, "")}`}>
                        <td className="py-2 text-[#E4E4E7]">{lessonLabel(r.path)}</td>
                        <td className="py-2 text-right font-['JetBrains_Mono'] text-[#F5A524]">{r.views}</td>
                        <td className="py-2 text-right font-['JetBrains_Mono'] text-[#A1A1AA]">{r.unique_students}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 print:px-8 print:py-4 print:max-w-none print:bg-white" data-testid="presenter-page">
        <div ref={printRef} className="bg-white text-black rounded-xl p-8 sm:p-10 print:p-0 print:rounded-none shadow-xl">
          <div className="text-center border-b-2 border-gray-200 pb-6 mb-8">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Presentation className="w-8 h-8 text-amber-500" weight="fill" />
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">{c.title}</h1>
            </div>
            <p className="text-lg text-gray-500">{c.subtitle}</p>
            <p className="text-sm text-red-500 font-medium mt-2 print:hidden">{c.admin_only}</p>
          </div>

          <div className="mb-8 bg-amber-50 border border-amber-200 rounded-lg p-5 break-inside-avoid">
            <h2 className="text-sm font-bold text-amber-800 uppercase tracking-wider mb-2">{c.setup_title}</h2>
            <ul className="text-sm text-amber-900 space-y-1">
              {c.setup.map((item, i) => (
                <li key={i}>• {item}</li>
              ))}
            </ul>
          </div>

          {/* Table of Contents — admin screen only, hidden in PDF */}
          <nav
            className="mb-8 bg-gray-50 border border-gray-200 rounded-lg p-5 print:hidden"
            data-testid="presenter-toc"
            aria-label="Presentation Table of Contents"
          >
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">
              {c.toc_label}
            </h2>
            <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
              {c.script.map((part, pi) => (
                <li key={pi} className="flex items-baseline gap-2 text-sm">
                  <span className="text-amber-500 font-bold tabular-nums w-6 shrink-0">
                    {String(pi + 1).padStart(2, "0")}
                  </span>
                  <a
                    href={`#part-${pi}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document
                        .getElementById(`part-${pi}`)
                        ?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    className="text-gray-700 hover:text-amber-600 transition-colors flex-1 truncate"
                    data-testid={`toc-part-${pi}`}
                    title={part.part}
                  >
                    {part.part}
                  </a>
                  <span className="text-xs text-gray-400 shrink-0">{part.time}</span>
                </li>
              ))}
            </ol>
          </nav>

          {c.script.map((part, pi) => (
            <div
              key={pi}
              id={`part-${pi}`}
              className="mb-8 break-inside-avoid scroll-mt-24"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-amber-500 text-white text-xs font-bold">{part.time}</span>
                <h2 className="text-xl font-bold text-gray-900">{part.part}</h2>
              </div>

              {part.scenes.map((scene, si) => (
                <div key={si} className="ml-4 mb-6 border-l-2 border-amber-300 pl-5">
                  <h3 className="font-bold text-gray-800 mb-2">
                    {scene.title}
                    {scene.time && <span className="text-gray-400 font-normal ml-2">({scene.time})</span>}
                  </h3>

                  {scene.say && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                      <span className="text-xs font-bold text-blue-600 uppercase">{c.say_label}</span>
                      <p className="text-sm text-blue-900 italic mt-1">"{scene.say}"</p>
                    </div>
                  )}

                  {scene.steps.length > 0 && (
                    <div className="mb-3">
                      <span className="text-xs font-bold text-gray-500 uppercase">{c.do_label}</span>
                      <ol className="mt-1 space-y-0.5">
                        {scene.steps.map((step, i) => (
                          <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                            {step.startsWith("  •") ? (
                              <span className="ml-6 text-gray-600">{step}</span>
                            ) : (
                              <>
                                <span className="w-5 h-5 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                                  {i + 1}
                                </span>
                                <span>{step}</span>
                              </>
                            )}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {scene.pointOut.length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <span className="text-xs font-bold text-green-600 uppercase">{c.point_out_label}</span>
                      <ul className="mt-1 space-y-1">
                        {scene.pointOut.map((point, i) => (
                          <li key={i} className="text-sm text-green-900 flex items-start gap-2">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}

          <div className="mt-8 break-inside-avoid">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{c.ref_title}</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    {c.ref_cols.map((col, i) => (
                      <th key={i} className="text-left p-2 border border-gray-200 font-semibold">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {c.settingsTable.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="p-2 border border-gray-200 font-medium">{row.scenario}</td>
                      <td className="p-2 border border-gray-200">{row.lens}</td>
                      <td className="p-2 border border-gray-200">{row.light}</td>
                      <td className="p-2 border border-gray-200">{row.type}</td>
                      <td className="p-2 border border-gray-200 text-amber-700 font-medium">{row.key}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t-2 border-gray-200 text-center">
            <p className="text-xs text-gray-400">{c.total_time}</p>
            <p className="text-xs text-gray-400 mt-1">www.dslradvisor.com</p>
          </div>
        </div>
      </div>
    </>
  );
}
